import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { APP_NAME } from '../config/frontend';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};


import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  Activity,
  PlusCircle,
  User,
  RefreshCw,
  Filter,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Calculator
} from 'lucide-react';
import toast from 'react-hot-toast';

const ProformaInvoice = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [proformaId, setProformaId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    customerId: '',
    phoneNumber: '',
    email: '',
    outstandingAmount: '₵0.00',
    address: ''
  });
  const [oneTimeCustomer, setOneTimeCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [isOneTimeCustomer, setIsOneTimeCustomer] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);
  const [isUomFocused, setIsUomFocused] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [workflowStep, setWorkflowStep] = useState('search'); // 'search', 'quantity', 'uom'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [currentProductQuantity, setCurrentProductQuantity] = useState(null);
  const [remark, setRemark] = useState('');
  const [salesAgent, setSalesAgent] = useState('');
  
  // Receipt Modal State
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [currencySettings, setCurrencySettings] = useState({
    currency_symbol: '₵',
    currency_code: 'GHS'
  });
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Grovalue Enterprise',
    company_address: '123 Business Street, City, Country',
    company_phone: '+233 XX XXX XXXX',
    company_email: '',
    company_website: '',
    tax_number: ''
  });

  // Bulk Reports State
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReportTab, setActiveReportTab] = useState('dashboard');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsData, setReportsData] = useState({
    dashboardStats: null,
    salesReport: null,
    productsReport: null,
    customersReport: null
  });
  
  // Bulk Sales History State
  const [bulkSalesHistory, setBulkSalesHistory] = useState([]);
  const [bulkSalesHistoryLoading, setBulkSalesHistoryLoading] = useState(false);
  const [bulkSalesHistorySearchTerm, setBulkSalesHistorySearchTerm] = useState('');
  const [bulkSalesHistoryFilter, setBulkSalesHistoryFilter] = useState('all');
  const [bulkSalesHistoryStartDate, setBulkSalesHistoryStartDate] = useState('');
  const [bulkSalesHistoryEndDate, setBulkSalesHistoryEndDate] = useState('');
  const [bulkSalesHistoryStatus, setBulkSalesHistoryStatus] = useState('all');
  
  // Action Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkSaleDeleteModal, setShowBulkSaleDeleteModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Proforma Reports state
  const [proformaReportsData, setProformaReportsData] = useState({
    summary: null,
    analytics: null,
    invoices: null
  });
  const [proformaReportsLoading, setProformaReportsLoading] = useState(false);
  const [proformaReportsDateRange, setProformaReportsDateRange] = useState({
    start_date: '2025-09-01', // Start from September 1st to include actual data
    end_date: new Date().toISOString().split('T')[0]
  });
  
  // Scroll state for proforma reports modal
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Edit Modal States
  const [editFormData, setEditFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    company_name: 'Grovalue Enterprise',
    sales_agent: '',
    sales_date: '',
    mode_of_sales: 'Cash',
    remark: '',
    total_amount: '',
    discount: 0
  });
  const [editLoading, setEditLoading] = useState(false);
  const [bulkSalesHistoryCurrentPage, setBulkSalesHistoryCurrentPage] = useState(1);
  const [bulkSalesHistoryPerPage, setBulkSalesHistoryPerPage] = useState(20);
  const [bulkSalesHistoryTotal, setBulkSalesHistoryTotal] = useState(0);
  const [bulkSalesHistorySortBy, setBulkSalesHistorySortBy] = useState('created_at');
  const [bulkSalesHistorySortOrder, setBulkSalesHistorySortOrder] = useState('desc');
  
  // Proforma Invoice History State
  const [proformaHistory, setProformaHistory] = useState([]);
  const [proformaHistoryLoading, setProformaHistoryLoading] = useState(false);
  const [proformaHistorySearchTerm, setProformaHistorySearchTerm] = useState('');
  const [proformaHistoryFilter, setProformaHistoryFilter] = useState('all');
  const [proformaHistoryStartDate, setProformaHistoryStartDate] = useState('');
  const [proformaHistoryEndDate, setProformaHistoryEndDate] = useState('');
  const [proformaHistoryStatus, setProformaHistoryStatus] = useState('all');
  const [proformaHistoryCurrentPage, setProformaHistoryCurrentPage] = useState(1);
  const [proformaHistoryPerPage, setProformaHistoryPerPage] = useState(20);
  const [proformaHistoryTotal, setProformaHistoryTotal] = useState(0);
  const [proformaHistorySortBy, setProformaHistorySortBy] = useState('created_at');
  const [proformaHistorySortOrder, setProformaHistorySortOrder] = useState('desc');

  // Invoice Management State
  const [showInvoiceManagementModal, setShowInvoiceManagementModal] = useState(false);
  const [selectedInvoiceForManagement, setSelectedInvoiceForManagement] = useState(null);
  const [invoiceManagementLoading, setInvoiceManagementLoading] = useState(false);
  const [invoiceManagementAction, setInvoiceManagementAction] = useState('');
  const [invoiceManagementNote, setInvoiceManagementNote] = useState('');
  const [showConvertToSaleModal, setShowConvertToSaleModal] = useState(false);
  const [conversionData, setConversionData] = useState({
    customer_id: null,
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    payment_method: 'Cash',
    payment_status: 'pending',
    delivery_date: '',
    delivery_address: '',
    notes: ''
  });

  // Helper function to format quantity display
  const formatQuantity = (quantity) => {
    if (typeof quantity !== 'number') return quantity;
    // If quantity is very close to a whole number (within 0.01), treat it as whole number
    if (Math.abs(quantity - Math.round(quantity)) < 0.01) {
      return Math.round(quantity);
    }
    return quantity.toFixed(2);
  };

  // Add loading state to prevent blank page
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Add error boundary to catch any rendering errors
  try {
    // Component logic will continue here
  } catch (error) {
    console.error('Error in ProformaInvoice component:', error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Something went wrong</h2>
          <p className="text-red-600 mb-4">Please refresh the page or contact support</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Initialize sales agent with current user's name
  useEffect(() => {
    if (user?.name) {
      setSalesAgent(user.name);
    }
  }, [user]);

  // Function to reset all Invoice Management modal state
  const resetInvoiceManagementModal = useCallback(() => {
    setSelectedInvoiceForManagement(null);
    setInvoiceManagementAction('');
    setInvoiceManagementNote('');
    setInvoiceManagementLoading(false);
    setShowConvertToSaleModal(false);
    setConversionData({
      customer_id: null,
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      payment_method: 'Cash',
      payment_status: 'pending',
      delivery_date: '',
      delivery_address: '',
      notes: ''
    });
    setProformaHistoryStartDate('');
    setProformaHistoryEndDate('');
    setProformaHistoryStatus('all');
  }, []);

  // Load proforma history when Invoice Management modal opens
  useEffect(() => {
    if (showInvoiceManagementModal) {
      // Use setTimeout to avoid potential race conditions
      setTimeout(() => {
        fetchProformaHistory();
      }, 100);
    }
  }, [showInvoiceManagementModal]);

  // Fetch currency and company settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/settings/receipt/info`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrencySettings({
            currency_symbol: data.data.currency_symbol || '₵',
            currency_code: data.data.currency_code || 'GHS'
          });
          setCompanySettings({
            company_name: data.data.company_name || 'Grovalue Enterprise',
            company_address: data.data.company_address || '123 Business Street, City, Country',
            company_phone: data.data.company_phone || '+233 XX XXX XXXX',
            company_email: data.data.company_email || '',
            company_website: data.data.company_website || '',
            tax_number: data.data.tax_number || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  // Format currency function
  const formatCurrency = useCallback((amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${currencySettings.currency_symbol} 0.00`;
    }
    
    const numAmount = parseFloat(amount);
    if (numAmount === 0) {
      return `${currencySettings.currency_symbol} 0.00`;
    }
    
    return `${currencySettings.currency_symbol} ${numAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }, [currencySettings.currency_symbol]);

  // Load settings on component mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);




  // Calculate totals
  const subtotal = useMemo(() => {
    return selectedProducts.reduce((sum, product) => sum + product.amount, 0);
  }, [selectedProducts]);

  const vat = useMemo(() => {
    return subtotal * 0.15; // 15% VAT
  }, [subtotal]);

  const grandTotal = useMemo(() => {
    return subtotal + vat - discount;
  }, [subtotal, vat, discount]);

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) {
      return customers;
    }
    return customers.filter(customer => 
      (customer.name || customer.customer_name || '')
        .toLowerCase()
        .includes(customerSearchQuery.toLowerCase())
    );
  }, [customers, customerSearchQuery]);


  const fetchCustomers = useCallback(async () => {
    try {
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      const data = await apiService.getCustomers('per_page=1000');
      
      const customersArray = data.customers || [];
      setCustomers(customersArray);
      
      // Force a re-render check
      setTimeout(() => {
      }, 100);
      
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      if (error.message === 'Authentication failed') {
        console.error('Authentication failed');
        toast.error('Authentication failed. Please login again.');
      } else {
        console.error('Error fetching customers:', error);
        toast.error('Error loading customers');
      }
    }
  }, []);

  // Debug customers state changes
  useEffect(() => {
  }, [customers]);

  // Debug customerInfo state changes
  useEffect(() => {
  }, [customerInfo]);

  const handleCustomerSelect = useCallback(async (customerId) => {
    if (!customerId) {
      setSelectedCustomer(null);
      setSelectedCustomerName('');
      setCustomerSearchQuery('');
      setCustomerInfo({
        customerId: '',
        phoneNumber: '',
        email: '',
        outstandingAmount: '₵0.00',
        address: ''
      });
      return;
    }

    try {
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      const response = await apiService.getCustomer(customerId);
      const customer = response.customer || response;
      
      setSelectedCustomer(customer);
      setSelectedCustomerName(customer.name || customer.customer_name || '');
      setCustomerSearchQuery('');
      setShowCustomerDropdown(false);
      const customerInfoData = {
        customerId: customer.customerID || customer.customer_id || customer.id || '',
        phoneNumber: customer.phone || '',
        email: customer.email || '',
        outstandingAmount: customer.balance ? `₵${parseFloat(customer.balance).toFixed(2)}` : '₵0.00',
        address: customer.address || ''
      };
      
      setCustomerInfo(customerInfoData);
    } catch (error) {
      if (error.message === 'Authentication failed') {
        console.error('Authentication failed');
        toast.error('Authentication failed. Please login again.');
      } else {
        console.error('Error fetching customer details:', error);
        toast.error('Error loading customer details');
      }
    }
  }, []);

  const handleCardClick = useCallback((href) => {
    console.log(`Navigating to: ${href}`);
    navigate(href);
  }, [navigate]);

    const generateBulkSaleId = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      // Make API call to get the last bulk sale ID from database
      const data = await apiService.getLastBulkSaleId();
      const lastId = data.lastId || 'BULK-20250101000'; // Default fallback
      
      // Extract the numeric part and increment
      const numericPart = parseInt(lastId.slice(-3)); // Get last 3 digits
      const newNumericPart = (numericPart + 1).toString().padStart(3, '0');
      
      // Generate new ID with current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      
      const newId = `BULK-${year}${month}${day}${newNumericPart}`;
      setBulkSaleId(newId);
    } catch (error) {
      console.error('Error generating bulk sale ID:', error);
      // Fallback: generate ID based on current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const fallbackId = `BULK-${year}${month}${day}001`;
      setBulkSaleId(fallbackId);
    } finally {
      setLoading(false);
    }
  }, []);

  const generateProformaId = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      // Make API call to get the next proforma ID from database
      const data = await apiService.getLastProformaId();
      
      const nextId = data.data?.next_id || 'PROF-20250101000'; // Default fallback
      
      setProformaId(nextId);
    } catch (error) {
      console.error('Error generating proforma ID:', error);
      // Fallback: generate ID based on current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const fallbackId = `PROF-${year}${month}${day}001`;
      setProformaId(fallbackId);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleModalOpen = useCallback((modalType) => {
    // Check if user is authenticated
    if (!apiService.isAuthenticated()) {
      toast.error('Please login to access this feature');
      return;
    }

    setActiveModal(modalType);
    setModalOpen(true);
    
    // Generate new proforma ID and fetch customers when opening create proforma modal
    if (modalType === 'create-proforma') {
      generateProformaId();
      fetchCustomers();
    }
  }, [generateProformaId, fetchCustomers]);

  const resetModalFields = useCallback(() => {
    
    setProformaId('');
    setSelectedCustomer(null);
    setCustomerInfo({
      customerId: '',
      phoneNumber: '',
      email: '',
      outstandingAmount: '₵0.00',
      address: ''
    });
    setOneTimeCustomer({
      name: '',
      phone: '',
      email: '',
      address: ''
    });
    setIsOneTimeCustomer(false);
    setSearchQuery('');
    setSelectedProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setSelectedCustomerName('');
    setCustomerSearchQuery('');
    setSelectedProduct(null);
    setWorkflowStep('search');
    setDiscount(0);
    setCurrentProductQuantity(null);
    setRemark('');
    setSalesAgent('');
    
    // Reset bulk sales history state
    setBulkSalesHistory([]);
    setBulkSalesHistoryLoading(false);
    setBulkSalesHistorySearchTerm('');
    setBulkSalesHistoryFilter('all');
    setBulkSalesHistoryStartDate('');
    setBulkSalesHistoryEndDate('');
    setBulkSalesHistoryStatus('all');
    setBulkSalesHistoryCurrentPage(1);
    setBulkSalesHistoryTotal(0);
    setBulkSalesHistorySortBy('created_at');
    setBulkSalesHistorySortOrder('desc');
    
    // Generate new proforma ID for the next entry
    generateProformaId();
    
    // Reinitialize sales agent with current user's name
    if (user?.name) {
      setSalesAgent(user.name);
    }
  }, [generateProformaId, user]);

  const handleSaveProforma = useCallback(async () => {
    // Check if customer is selected or one-time customer info is filled
    if (!isOneTimeCustomer && !selectedCustomer) {
      toast.error('Please select a customer first!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
      return;
    }

    if (isOneTimeCustomer && !oneTimeCustomer.name.trim()) {
      toast.error('Please enter customer name for one-time customer!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product to the cart!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
      return;
    }

    try {
      setLoading(true);
      
      const proformaData = {
        customer_id: isOneTimeCustomer ? null : selectedCustomer.id,
        customer_name: isOneTimeCustomer ? oneTimeCustomer.name : null,
        customer_phone: isOneTimeCustomer ? oneTimeCustomer.phone : null,
        customer_email: isOneTimeCustomer ? oneTimeCustomer.email : null,
        customer_address: isOneTimeCustomer ? oneTimeCustomer.address : null,
        invoice_date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        remark: remark,
        sales_agent: salesAgent,
        items: selectedProducts.map(product => ({
          product_id: product.id,
          quantity: product.quantity,
          unit_of_measure: product.uom,
          price: product.price
        })),
        discount: discount,
        vat_rate: 15
      };

      const response = await apiService.createProformaInvoice(proformaData);
      
      // Update the proformaId with the new ID from the response
      const newProformaId = response.data?.proforma_id || proformaId;
      setProformaId(newProformaId);
      
      // Prepare receipt data
      const receipt = {
        proformaId: newProformaId,
        customer: isOneTimeCustomer ? {
          name: oneTimeCustomer.name,
          phone: oneTimeCustomer.phone,
          email: oneTimeCustomer.email,
          address: oneTimeCustomer.address
        } : {
          name: selectedCustomer.name || selectedCustomer.customer_name,
          phone: selectedCustomer.phone,
          email: selectedCustomer.email,
          address: selectedCustomer.address
        },
        items: selectedProducts,
        subtotal: subtotal,
        vat: vat,
        discount: discount,
        grandTotal: grandTotal,
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        status: 'active',
        salesAgent: salesAgent
      };

      // Set receipt data and show receipt modal
      setReceiptData(receipt);
      setShowReceiptModal(true);
      
      toast.success('Proforma invoice created successfully!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });

      // Keep the modal open and form populated for next sale
      // Clear the selected products and customer information to start fresh
      setSelectedProducts([]);
      setDiscount(0);
      setRemark('');
      
      // Reset customer information fields
      setSelectedCustomer(null);
      setSelectedCustomerName('');
      setCustomerInfo({
        customerId: '',
        phoneNumber: '',
        email: '',
        outstandingAmount: '₵0.00',
        address: ''
      });
      setOneTimeCustomer({
        name: '',
        phone: '',
        email: '',
        address: ''
      });
      setIsOneTimeCustomer(false);
      setCustomerSearchQuery('');
      setShowCustomerDropdown(false);
      
      // Generate new proforma ID for the next entry
      generateProformaId();
      
    } catch (error) {
      console.error('Error creating proforma invoice:', error);
      toast.error('Failed to create proforma invoice. Please try again.', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer, selectedProducts, proformaId, subtotal, vat, discount, grandTotal, remark, user]);

  // Fetch bulk sales history
  const fetchBulkSalesHistory = useCallback(async (searchTerm = null) => {
    setBulkSalesHistoryLoading(true);
    try {
      // Build query parameters
      const params = {
        sort_by: bulkSalesHistorySortBy,
        sort_order: bulkSalesHistorySortOrder
      };

      // Use provided search term or current state
      const finalSearchTerm = searchTerm !== null ? searchTerm : bulkSalesHistorySearchTerm;
      if (finalSearchTerm && finalSearchTerm.trim()) {
        params.search = finalSearchTerm.trim();
      }

      if (bulkSalesHistoryStartDate) {
        params.start_date = bulkSalesHistoryStartDate;
      }
      
      if (bulkSalesHistoryEndDate) {
        params.end_date = bulkSalesHistoryEndDate;
      }

      if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') {
        params.status = bulkSalesHistoryStatus;
      }

      const response = await apiService.getBulkSalesHistory(params);
      
      if (response.success) {
        setBulkSalesHistory(response.data.bulk_sales || []);
        setBulkSalesHistoryTotal(response.data.total || 0);
      } else {
        console.error('Error fetching bulk sales history:', response.message);
        toast.error('Failed to load bulk sales history', {
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        });
      }
    } catch (error) {
      console.error('Error fetching bulk sales history:', error);
      toast.error('Network error while loading bulk sales history', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setBulkSalesHistoryLoading(false);
    }
  }, [bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  // Fetch proforma invoice history
  const fetchProformaHistory = useCallback(async (searchTerm = null) => {
    setProformaHistoryLoading(true);
    try {
      // Build query parameters
      const params = {
        sort_by: proformaHistorySortBy,
        sort_order: proformaHistorySortOrder
      };

      // Use provided search term or current state
      const finalSearchTerm = searchTerm !== null ? searchTerm : proformaHistorySearchTerm;
      if (finalSearchTerm && finalSearchTerm.trim()) {
        params.search = finalSearchTerm.trim();
      }

      if (proformaHistoryStartDate) {
        params.start_date = proformaHistoryStartDate;
      }
      
      if (proformaHistoryEndDate) {
        params.end_date = proformaHistoryEndDate;
      }

      if (proformaHistoryStatus && proformaHistoryStatus !== 'all') {
        params.status = proformaHistoryStatus;
      }

      const response = await apiService.getProformaInvoices(params);
      
      if (response.success) {
        setProformaHistory(response.data.proforma_invoices || []);
        setProformaHistoryTotal(response.data.total || 0);
      } else {
        console.error('❌ Error fetching proforma history:', response.message);
        toast.error('Failed to load proforma history', {
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        });
      }
    } catch (error) {
      console.error('💥 Error fetching proforma history:', error);
      toast.error('Network error while loading proforma history', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setProformaHistoryLoading(false);
    }
  }, [proformaHistorySortBy, proformaHistorySortOrder, proformaHistoryStartDate, proformaHistoryEndDate, proformaHistoryStatus, proformaHistorySearchTerm]);

  // Load data when modal opens
  useEffect(() => {
    if (activeModal === 'bulk-sales-history') {
      fetchBulkSalesHistory();
    } else if (activeModal === 'invoice-history') {
      fetchProformaHistory();
    }
  }, [activeModal]);

  // Load reports data when bulk-reports modal opens
  useEffect(() => {
    if (activeModal === 'bulk-reports') {
      fetchBulkReportsData();
    }
  }, [activeModal]);

  // Debug useEffect for pagination
  useEffect(() => {
  }, [bulkSalesHistoryCurrentPage]);

  // Handle bulk sales history modal open
  const handleBulkSalesHistoryOpen = useCallback(() => {
    handleModalOpen('invoice-history');
  }, []);

  // Debounced search function for proforma history
  const debouncedProformaHistorySearch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        fetchProformaHistory(searchTerm);
      }
    }, 500),
    [fetchProformaHistory]
  );

  // Handle proforma history search
  const handleProformaHistorySearch = useCallback((searchTerm) => {
    setProformaHistorySearchTerm(searchTerm);
    debouncedProformaHistorySearch(searchTerm);
  }, [debouncedProformaHistorySearch]);

  // Handle proforma history filter change
  const handleProformaHistoryFilterChange = useCallback((filter) => {
    setProformaHistoryFilter(filter);
  }, []);

  // Handle apply proforma history filter
  const handleApplyProformaHistoryFilter = useCallback(() => {
    // Filter is handled by useEffect
  }, []);

  // Handle today filter for proforma history
  const handleProformaHistoryTodayFilter = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setProformaHistoryStartDate(today);
    setProformaHistoryEndDate(today);
    setProformaHistoryStatus('all');
    setProformaHistoryCurrentPage(1);
  }, []);

  // Debounced search function for bulk sales history
  const debouncedBulkSalesSearch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        fetchBulkSalesHistory(searchTerm);
      }
    }, 500),
    [fetchBulkSalesHistory]
  );

  // Handle bulk sales history search
  const handleBulkSalesHistorySearch = useCallback((searchTerm) => {
    setBulkSalesHistorySearchTerm(searchTerm);
    debouncedBulkSalesSearch(searchTerm);
  }, [debouncedBulkSalesSearch]);

  // Handle bulk sales history filter change
  const handleBulkSalesHistoryFilterChange = useCallback((filter) => {
    setBulkSalesHistoryFilter(filter);
  }, []);

  // Handle apply bulk sales history filter
  const handleApplyBulkSalesHistoryFilter = useCallback(() => {
    // Filter is handled by useEffect
  }, []);

  // Handle today filter
  const handleTodayFilter = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setBulkSalesHistoryStartDate(today);
    setBulkSalesHistoryEndDate(today);
    setBulkSalesHistoryStatus('all');
    setBulkSalesHistoryCurrentPage(1);
  }, []);

  // Handle pagination
  const handlePreviousPage = useCallback(async () => {
    if (bulkSalesHistoryCurrentPage > 1) {
      const newPage = bulkSalesHistoryCurrentPage - 1;
      setBulkSalesHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setBulkSalesHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: bulkSalesHistoryPerPage,
          sort_by: bulkSalesHistorySortBy,
          sort_order: bulkSalesHistorySortOrder
        };
        
        if (bulkSalesHistoryStartDate) params.start_date = bulkSalesHistoryStartDate;
        if (bulkSalesHistoryEndDate) params.end_date = bulkSalesHistoryEndDate;
        if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') params.status = bulkSalesHistoryStatus;
        if (bulkSalesHistorySearchTerm) params.search = bulkSalesHistorySearchTerm;

        const response = await apiService.getBulkSalesHistory(params);
        if (response.success) {
          setBulkSalesHistory(response.data.bulk_sales || []);
          setBulkSalesHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setBulkSalesHistoryLoading(false);
      }
    }
  }, [bulkSalesHistoryCurrentPage, bulkSalesHistoryPerPage, bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  const handleNextPage = useCallback(async () => {
    const totalPages = Math.ceil(bulkSalesHistoryTotal / bulkSalesHistoryPerPage);
    if (bulkSalesHistoryCurrentPage < totalPages) {
      const newPage = bulkSalesHistoryCurrentPage + 1;
      setBulkSalesHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setBulkSalesHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: bulkSalesHistoryPerPage,
          sort_by: bulkSalesHistorySortBy,
          sort_order: bulkSalesHistorySortOrder
        };
        
        if (bulkSalesHistoryStartDate) params.start_date = bulkSalesHistoryStartDate;
        if (bulkSalesHistoryEndDate) params.end_date = bulkSalesHistoryEndDate;
        if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') params.status = bulkSalesHistoryStatus;
        if (bulkSalesHistorySearchTerm) params.search = bulkSalesHistorySearchTerm;

        const response = await apiService.getBulkSalesHistory(params);
        if (response.success) {
          setBulkSalesHistory(response.data.bulk_sales || []);
          setBulkSalesHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setBulkSalesHistoryLoading(false);
      }
    }
  }, [bulkSalesHistoryCurrentPage, bulkSalesHistoryTotal, bulkSalesHistoryPerPage, bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  // Handle sorting
  const handleSort = useCallback((column) => {
    if (bulkSalesHistorySortBy === column) {
      setBulkSalesHistorySortBy(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setBulkSalesHistorySortBy(column);
      setBulkSalesHistorySortOrder('asc');
    }
    setBulkSalesHistoryCurrentPage(1); // Reset to first page
  }, [bulkSalesHistorySortBy, bulkSalesHistorySortOrder]);

  // Handle proforma history pagination
  const handleProformaHistoryPreviousPage = useCallback(async () => {
    if (proformaHistoryCurrentPage > 1) {
      const newPage = proformaHistoryCurrentPage - 1;
      setProformaHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setProformaHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: proformaHistoryPerPage,
          sort_by: proformaHistorySortBy,
          sort_order: proformaHistorySortOrder
        };
        
        if (proformaHistoryStartDate) params.start_date = proformaHistoryStartDate;
        if (proformaHistoryEndDate) params.end_date = proformaHistoryEndDate;
        if (proformaHistoryStatus && proformaHistoryStatus !== 'all') params.status = proformaHistoryStatus;
        if (proformaHistorySearchTerm) params.search = proformaHistorySearchTerm;

        const response = await apiService.getProformaInvoices(params);
        if (response.success) {
          setProformaHistory(response.data.proforma_invoices || []);
          setProformaHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching proforma history page:', error);
      } finally {
        setProformaHistoryLoading(false);
      }
    }
  }, [proformaHistoryCurrentPage, proformaHistoryPerPage, proformaHistorySortBy, proformaHistorySortOrder, proformaHistoryStartDate, proformaHistoryEndDate, proformaHistoryStatus, proformaHistorySearchTerm]);

  const handleProformaHistoryNextPage = useCallback(async () => {
    const totalPages = Math.ceil(proformaHistoryTotal / proformaHistoryPerPage);
    if (proformaHistoryCurrentPage < totalPages) {
      const newPage = proformaHistoryCurrentPage + 1;
      setProformaHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setProformaHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: proformaHistoryPerPage,
          sort_by: proformaHistorySortBy,
          sort_order: proformaHistorySortOrder
        };
        
        if (proformaHistoryStartDate) params.start_date = proformaHistoryStartDate;
        if (proformaHistoryEndDate) params.end_date = proformaHistoryEndDate;
        if (proformaHistoryStatus && proformaHistoryStatus !== 'all') params.status = proformaHistoryStatus;
        if (proformaHistorySearchTerm) params.search = proformaHistorySearchTerm;

        const response = await apiService.getProformaInvoices(params);
        if (response.success) {
          setProformaHistory(response.data.proforma_invoices || []);
          setProformaHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching proforma history page:', error);
      } finally {
        setProformaHistoryLoading(false);
      }
    }
  }, [proformaHistoryCurrentPage, proformaHistoryPerPage, proformaHistorySortBy, proformaHistorySortOrder, proformaHistoryStartDate, proformaHistoryEndDate, proformaHistoryStatus, proformaHistorySearchTerm]);

  // Handle proforma history sort
  const handleProformaHistorySort = useCallback((column) => {
    if (proformaHistorySortBy === column) {
      setProformaHistorySortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setProformaHistorySortBy(column);
      setProformaHistorySortOrder('asc');
    }
    setProformaHistoryCurrentPage(1); // Reset to first page
  }, [proformaHistorySortBy, proformaHistorySortOrder]);

  // Handle bulk sale selection
  const handleBulkSaleSelect = useCallback((sale) => {
    // TODO: Implement bulk sale details view
    toast.success(`Viewing bulk sale: ${sale.bulk_sale_id}`, {
      style: {
        background: '#1f2937',
        color: '#fff',
        border: '1px solid #374151'
      }
    });
  }, []);

  // Handle print existing receipt
  const handlePrintExistingReceipt = useCallback(async (sale) => {
    try {
      // Fetch complete bulk sale details including items
      const response = await apiService.getBulkSaleDetails(sale.id);
      const saleDetails = response.data;
      
      
      // Validate that items exist and have the expected structure
      if (!saleDetails.items || !Array.isArray(saleDetails.items)) {
        throw new Error('No items found for this sale');
      }
      
      // Prepare receipt data in the same format as create bulk sale receipt
      const receiptData = {
        proformaId: saleDetails.proforma_id,
        date: new Date(saleDetails.created_at).toLocaleDateString(),
        time: new Date(saleDetails.created_at).toLocaleTimeString(),
        customer: {
          name: saleDetails.customer_name || 'N/A',
          phone: saleDetails.customer_phone || 'N/A',
          email: saleDetails.customer_email || 'N/A',
          address: saleDetails.customer_address || 'N/A'
        },
        items: saleDetails.items.map(item => {
          console.log('Processing item:', item);
          return {
            name: item.product_name || 'Unknown Product',
            quantity: item.quantity || 0,
            uom: item.uom || 'N/A',
            price: parseFloat(item.unit_price || 0),
            amount: parseFloat(item.total_amount || 0)
          };
        }),
        subtotal: saleDetails.items.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0),
        vat: parseFloat(saleDetails.vat || 0),
        discount: parseFloat(saleDetails.discount || 0),
        grandTotal: parseFloat(saleDetails.total_amount || 0)
      };
      
      setReceiptData(receiptData);
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load sale details for printing', {
        duration: 4000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          border: '1px solid #DC2626'
        }
      });
    }
  }, []);

  // Handle edit sale
  const handleEditSale = useCallback(async (sale) => {
    try {
      setEditLoading(true);
      
      // Fetch complete bulk sale details including items
      const response = await apiService.getBulkSaleDetails(sale.id);
      const saleDetails = response.data;
      
      setSelectedSale(saleDetails);
      
      // Populate form data with fetched details
    setEditFormData({
        customer_name: saleDetails.customer_name || '',
        customer_email: saleDetails.customer_email || '',
        customer_phone: saleDetails.customer_phone || '',
        customer_address: saleDetails.customer_address || '',
        company_name: saleDetails.company_name || 'Grovalue Enterprise',
        sales_agent: saleDetails.sales_agent || '',
        sales_date: new Date(saleDetails.created_at).toLocaleDateString('en-GB'),
        mode_of_sales: saleDetails.mode_of_sales || 'Cash',
        remark: saleDetails.remark || '',
        total_amount: saleDetails.total_amount || '',
        discount: saleDetails.discount || 0
      });
      
      // Populate selected products with fetched items
      if (saleDetails.items && saleDetails.items.length > 0) {
        const formattedProducts = saleDetails.items.map(item => ({
          id: item.product_id,
          name: item.product_name,
          quantity: parseFloat(item.quantity),
          uom: item.uom,
          price: parseFloat(item.unit_price),
          amount: parseFloat(item.total_amount)
        }));
        setSelectedProducts(formattedProducts);
      } else {
    setSelectedProducts([]);
      }
      
      // Set discount
      setDiscount(saleDetails.discount || 0);
      
    setShowEditModal(true);
      
    } catch (error) {
      console.error('Error fetching bulk sale details:', error);
      toast.error('Failed to load bulk sale details', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setEditLoading(false);
    }
  }, []);

  // Handle edit form submission
  const handleEditSubmit = useCallback(async () => {
    if (!selectedSale) return;
    
    setEditLoading(true);
    try {
      // Calculate new total with discount and products
      const productsTotal = selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0);
      const discountAmount = parseFloat(editFormData.discount) || 0;
      const newTotal = productsTotal - discountAmount;
      
      // Prepare data for API call
      const updateData = {
        customer_name: editFormData.customer_name,
        customer_email: editFormData.customer_email,
        customer_phone: editFormData.customer_phone,
        customer_address: editFormData.customer_address,
        company_name: editFormData.company_name,
        sales_agent: editFormData.sales_agent,
        sales_date: editFormData.sales_date,
        mode_of_sales: editFormData.mode_of_sales,
        remark: editFormData.remark,
        discount: discountAmount,
        status: 'completed',
        items: selectedProducts.map(product => ({
          product_id: product.id,
          product_name: product.name,
          quantity: product.quantity,
          uom: product.uom,
          unit_price: product.price,
          total_amount: product.amount
        }))
      };
      

      
      // Call API to update the bulk sale
      const response = await apiService.updateBulkSale(selectedSale.id, updateData);
      
      if (response.success) {
      // Update the sale in the list
      setBulkSalesHistory(prev => prev.map(sale => 
        sale.id === selectedSale.id 
          ? {
              ...sale,
              customer_name: editFormData.customer_name,
              customer_email: editFormData.customer_email,
              customer_phone: editFormData.customer_phone,
              customer_address: editFormData.customer_address,
              sales_agent: editFormData.sales_agent,
              mode_of_sales: editFormData.mode_of_sales,
              remark: editFormData.remark,
                total_amount: response.data.grand_total.toString(),
              discount: discountAmount
            }
          : sale
      ));
      
      // Update selected sale
      setSelectedSale(prev => ({
        ...prev,
        customer_name: editFormData.customer_name,
        customer_email: editFormData.customer_email,
        customer_phone: editFormData.customer_phone,
        customer_address: editFormData.customer_address,
        sales_agent: editFormData.sales_agent,
        mode_of_sales: editFormData.mode_of_sales,
        remark: editFormData.remark,
          total_amount: response.data.grand_total.toString(),
        discount: discountAmount
      }));
      
      setShowEditModal(false);
        toast.success(`Bulk sale ${selectedSale.bulk_sale_id} updated successfully! 🎉`, {
          duration: 3000,
          icon: '✅',
        style: {
            background: '#10B981',
          color: '#fff',
            border: '1px solid #059669'
        }
      });
      } else {
        throw new Error(response.message || 'Failed to update bulk sale');
      }
    } catch (error) {
      console.error('Error updating bulk sale:', error);
      toast.error('Failed to update bulk sale. Please try again.', {
        duration: 4000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          border: '1px solid #DC2626'
        }
      });
    } finally {
      setEditLoading(false);
    }
  }, [selectedSale, editFormData, selectedProducts]);

  // Proforma Invoice Action Handlers
  const handleProformaInvoiceSelect = useCallback((invoice) => {
    // TODO: Implement proforma invoice details view
    toast.success(`Viewing proforma invoice: ${invoice.proforma_id}`, {
      style: {
        background: '#1f2937',
        color: '#fff',
        border: '1px solid #374151'
      }
    });
  }, []);

  // Handle print existing proforma receipt
  const handlePrintExistingProformaReceipt = useCallback(async (invoice) => {
    try {
      // Fetch complete proforma invoice details including items
      const response = await apiService.getProformaInvoiceDetails(invoice.id);
      const invoiceDetails = response.data;
      
      
      // Validate that items exist and have the expected structure
      if (!invoiceDetails.items || !Array.isArray(invoiceDetails.items)) {
        throw new Error('No items found for this proforma invoice');
      }
      
      // Prepare receipt data in the same format as create proforma receipt
      const receiptData = {
        proformaId: invoiceDetails.proforma_id,
        date: new Date(invoiceDetails.created_at).toLocaleDateString(),
        time: new Date(invoiceDetails.created_at).toLocaleTimeString(),
        customer: {
          id: invoiceDetails.customer?.customerID || invoiceDetails.customer_id || null,
          name: invoiceDetails.customer_name || invoiceDetails.customer?.name || 'N/A',
          phone: invoiceDetails.customer_phone || invoiceDetails.customer?.phone || 'N/A',
          email: invoiceDetails.customer_email || invoiceDetails.customer?.email || 'N/A',
          address: invoiceDetails.customer_address || invoiceDetails.customer?.address || 'N/A'
        },
        items: invoiceDetails.items.map(item => {
          console.log('Processing item:', item);
          
          // Normalize quantity to whole number if it's very close to one
          let normalizedQuantity = item.quantity || 0;
          if (Math.abs(normalizedQuantity - Math.round(normalizedQuantity)) < 0.01) {
            normalizedQuantity = Math.round(normalizedQuantity);
          }
          
          return {
            name: item.product?.name || 'Unknown Product',
            quantity: normalizedQuantity,
            uom: item.unit_of_measure || 'N/A',
            price: parseFloat(item.price || 0),
            amount: parseFloat(item.amount || 0)
          };
        }),
        subtotal: invoiceDetails.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0),
        vat: parseFloat(invoiceDetails.vat_amount || 0),
        discount: parseFloat(invoiceDetails.discount || 0),
        grandTotal: parseFloat(invoiceDetails.grand_total || 0),
        salesAgent: invoiceDetails.created_by_name || 'System'
      };
      
      
      // Also set selectedProducts so the receipt modal can access product data
      if (invoiceDetails.items && Array.isArray(invoiceDetails.items)) {
        const products = invoiceDetails.items.map(item => ({
          id: item.product_id,
          name: item.product?.name || 'Unknown Product',
          quantity: item.quantity,
          uom: item.unit_of_measure,
          price: parseFloat(item.price),
          amount: parseFloat(item.amount)
        }));
        setSelectedProducts(products);
      }
      
      setReceiptData(receiptData);
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error fetching proforma invoice details:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load proforma invoice details for printing', {
        duration: 4000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          border: '1px solid #DC2626'
        }
      });
    }
  }, []);

  // Handle edit proforma invoice
  const handleEditProformaInvoice = useCallback(async (invoice) => {
    try {
      setEditLoading(true);
      
      // Fetch complete proforma invoice details including items
      const response = await apiService.getProformaInvoiceDetails(invoice.id);
      const invoiceDetails = response.data;
      
      
      setSelectedSale(invoiceDetails);
      
      // Populate form data with fetched details
      setEditFormData({
        customer_name: invoiceDetails.customer_name || invoiceDetails.customer?.name || '',
        customer_email: invoiceDetails.customer_email || invoiceDetails.customer?.email || '',
        customer_phone: invoiceDetails.customer_phone || invoiceDetails.customer?.phone || '',
        customer_address: invoiceDetails.customer_address || invoiceDetails.customer?.address || '',
        company_name: 'Grovalue Enterprise',
        sales_agent: user?.name || 'System',
        sales_date: invoiceDetails.invoice_date ? new Date(invoiceDetails.invoice_date).toLocaleDateString('en-GB') : '',
        mode_of_sales: 'Cash',
        remark: invoiceDetails.remark || '',
        total_amount: invoiceDetails.grand_total || '',
        discount: invoiceDetails.discount || 0
      });
      
      // Set selected products
      if (invoiceDetails.items && Array.isArray(invoiceDetails.items)) {
        const products = invoiceDetails.items.map(item => {
          // Normalize quantity to whole number if it's very close to one
          let normalizedQuantity = item.quantity;
          if (Math.abs(normalizedQuantity - Math.round(normalizedQuantity)) < 0.01) {
            normalizedQuantity = Math.round(normalizedQuantity);
          }
          
          return {
            id: item.product_id,
            name: item.product?.name || 'Unknown Product',
            quantity: normalizedQuantity,
            uom: item.unit_of_measure,
            price: parseFloat(item.price),
            amount: parseFloat(item.amount)
          };
        });
        setSelectedProducts(products);
      } else {
        setSelectedProducts([]);
      }
      
      // Set discount
      setDiscount(invoiceDetails.discount || 0);
      
      setShowEditModal(true);
      
    } catch (error) {
      console.error('Error fetching proforma invoice details:', error);
      toast.error('Failed to load proforma invoice details', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setEditLoading(false);
    }
  }, []);

  // Bulk Reports Functions
  const handleReportsDateChange = useCallback((field, value) => {
    setReportsDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleApplyReportsFilter = useCallback(async () => {
    setReportsLoading(true);
    try {
      // Fetch reports data based on date range
      await fetchBulkReportsData();
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to fetch reports data');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const handleTodayReportsFilter = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    setReportsDateRange({
      startDate: today,
      endDate: today
    });
    
    setReportsLoading(true);
    try {
      await fetchBulkReportsData();
    } catch (error) {
      console.error('Error fetching today\'s reports:', error);
      toast.error('Failed to fetch today\'s reports');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const fetchBulkReportsData = useCallback(async () => {
    try {
      // Fetch dashboard stats
      const dashboardResponse = await apiService.getBulkReportsDashboard({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (dashboardResponse.success) {
        setReportsData(prev => ({
          ...prev,
          dashboardStats: dashboardResponse.data
        }));
      }

      // Fetch sales report
      const salesResponse = await apiService.getBulkReportsSales({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (salesResponse.success) {
        setReportsData(prev => ({
          ...prev,
          salesReport: salesResponse.data
        }));
      }

      // Fetch products report
      const productsResponse = await apiService.getBulkReportsProducts({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (productsResponse.success) {
        setReportsData(prev => ({
          ...prev,
          productsReport: productsResponse.data
        }));
      }

      // Fetch customers report
      const customersResponse = await apiService.getBulkReportsCustomers({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (customersResponse.success) {
        setReportsData(prev => ({
          ...prev,
          customersReport: customersResponse.data
        }));
      }
    } catch (error) {
      console.error('Error fetching bulk reports data:', error);
      throw error;
    }
  }, [reportsDateRange.startDate, reportsDateRange.endDate]);

  // Handle delete sale
  const handleDeleteSale = useCallback((sale) => {
    setSelectedSale(sale);
    setShowBulkSaleDeleteModal(true);
  }, []);

  // Handle export to Excel
  const handleExportToExcel = useCallback(() => {
    try {
      // Create CSV content
      const headers = ['BulkSale ID', 'Customer Name', 'Email', 'Phone Number', 'Amount', 'Status', 'Date'];
      const csvContent = [
        headers.join(','),
        ...bulkSalesHistory.map(sale => [
          sale.bulk_sale_id,
          sale.customer_name || 'N/A',
          sale.customer_email || 'N/A',
          sale.customer_phone || 'N/A',
          parseFloat(sale.total_amount).toFixed(2),
          sale.status || 'N/A',
          new Date(sale.created_at).toLocaleDateString()
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bulk_sales_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Excel file exported successfully!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [bulkSalesHistory]);

  // Handle export to Excel for proforma invoices
  const handleProformaExportToExcel = useCallback(() => {
    try {
      // Create CSV content
      const headers = ['Proforma ID', 'Customer Name', 'Email', 'Phone Number', 'Amount', 'Status', 'Date'];
      const csvContent = [
        headers.join(','),
        ...proformaHistory.map(invoice => [
          invoice.proforma_id,
          invoice.customer_name || invoice.customer?.name || 'N/A',
          invoice.customer_email || invoice.customer?.email || 'N/A',
          invoice.customer_phone || invoice.customer?.phone || 'N/A',
          parseFloat(invoice.grand_total).toFixed(2),
          invoice.status || 'N/A',
          new Date(invoice.created_at).toLocaleDateString()
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `proforma_invoices_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Proforma invoices Excel file exported successfully!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting proforma invoices to Excel:', error);
      toast.error('Failed to export proforma invoices Excel file', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [proformaHistory]);

  // Invoice Management Functions
  const handleInvoiceManagement = useCallback((invoice, action) => {
    setSelectedInvoiceForManagement(invoice);
    setInvoiceManagementAction(action);
    setInvoiceManagementNote('');
    setShowInvoiceManagementModal(true);
  }, []);

  const handleConvertToSale = useCallback((invoice) => {
    setSelectedInvoiceForManagement(invoice);
    
    // Pre-populate conversion data with invoice details
    setConversionData({
      customer_id: invoice.customer_id,
      customer_name: invoice.customer_name || invoice.customer?.name || '',
      customer_phone: invoice.customer_phone || invoice.customer?.phone || '',
      customer_email: invoice.customer_email || invoice.customer?.email || '',
      customer_address: invoice.customer_address || invoice.customer?.address || '',
      payment_method: 'Cash',
      payment_status: 'pending',
      delivery_date: new Date().toISOString().split('T')[0],
      delivery_address: invoice.customer_address || invoice.customer?.address || '',
      notes: `Converted from Proforma Invoice: ${invoice.proforma_id}`
    });
    
    setShowConvertToSaleModal(true);
  }, []);

  const handleInvoiceAction = useCallback(async () => {
    if (!selectedInvoiceForManagement || !invoiceManagementAction) return;

    try {
      setInvoiceManagementLoading(true);
      
      let endpoint = '';
      let data = {};
      
      switch (invoiceManagementAction) {
        case 'cancel':
          endpoint = `/proforma-invoices/${selectedInvoiceForManagement.id}/cancel`;
          data = { note: invoiceManagementNote };
          break;
        case 'reactivate':
          endpoint = `/proforma-invoices/${selectedInvoiceForManagement.id}/reactivate`;
          data = { note: invoiceManagementNote };
          break;
        case 'extend':
          endpoint = `/proforma-invoices/${selectedInvoiceForManagement.id}/extend`;
          data = { 
            valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            note: invoiceManagementNote 
          };
          break;
        default:
          throw new Error('Invalid action');
      }

      
      const response = await apiService.request(endpoint, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      
      if (response.success) {
        toast.success(`Invoice ${invoiceManagementAction}ed successfully!`, {
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        });
        
        // Refresh the invoice history
        fetchProformaHistory();
        
        // Close the modal and reset everything
        setShowInvoiceManagementModal(false);
        resetInvoiceManagementModal();
      } else {
        throw new Error(response.message || 'Action failed');
      }
    } catch (error) {
      console.error('Error performing invoice action:', error);
      toast.error(`Failed to ${invoiceManagementAction} invoice: ${error.message}`, {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setInvoiceManagementLoading(false);
    }
  }, [selectedInvoiceForManagement, invoiceManagementAction, invoiceManagementNote, fetchProformaHistory]);

  const handleConvertInvoiceToSale = useCallback(async () => {
    if (!selectedInvoiceForManagement) return;

    try {
      setInvoiceManagementLoading(true);
      
      const conversionPayload = {
        proforma_invoice_id: selectedInvoiceForManagement.id,
        customer_id: conversionData.customer_id,
        customer_name: conversionData.customer_name,
        customer_phone: conversionData.customer_phone,
        customer_email: conversionData.customer_email,
        customer_address: conversionData.customer_address,
        payment_method: conversionData.payment_method,
        payment_status: conversionData.payment_status,
        delivery_date: conversionData.delivery_date,
        delivery_address: conversionData.delivery_address,
        notes: conversionData.notes,
        items: selectedInvoiceForManagement.items?.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_of_measure: item.unit_of_measure,
          price: item.price,
          amount: item.amount
        })) || []
      };

      
      const response = await apiService.request('/proforma-invoices/convert-to-sale', {
        method: 'POST',
        body: JSON.stringify(conversionPayload)
      });
      
      
      if (response.success) {
        toast.success('Invoice converted to sale successfully!', {
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        });
        
        // Refresh the invoice history
        fetchProformaHistory();
        
        // Close the modal and reset everything
        setShowConvertToSaleModal(false);
        resetInvoiceManagementModal();
      } else {
        throw new Error(response.message || 'Conversion failed');
      }
    } catch (error) {
      console.error('Error converting invoice to sale:', error);
      toast.error(`Failed to convert invoice: ${error.message}`, {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setInvoiceManagementLoading(false);
    }
  }, [selectedInvoiceForManagement, conversionData, fetchProformaHistory]);

  const getStatusBadgeColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'converted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }, []);

  const getStatusBadgeText = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'converted':
        return 'Converted';
      case 'cancelled':
        return 'Cancelled';
      case 'expired':
        return 'Expired';
      default:
        return 'Unknown';
    }
  }, []);

  // Handle export to PDF
  const handleExportToPDF = useCallback(() => {
    try {
      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Bulk Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            .date { text-align: center; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { color: #008000; font-weight: bold; }
            .status { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
            .completed { background-color: #d4edda; color: #155724; }
            .pending { background-color: #fff3cd; color: #856404; }
            .cancelled { background-color: #f8d7da; color: #721c24; }
          </style>
        </head>
        <body>
          <h1>Bulk Sales Report</h1>
          <div class="date">Generated on: ${new Date().toLocaleDateString()}</div>
          <table>
            <thead>
              <tr>
                <th>BulkSale ID</th>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${bulkSalesHistory.map(sale => `
                <tr>
                  <td>${sale.bulk_sale_id}</td>
                  <td>${sale.customer_name || 'N/A'}</td>
                  <td>${sale.customer_email || 'N/A'}</td>
                  <td>${sale.customer_phone || 'N/A'}</td>
                  <td class="amount">Ghs ${parseFloat(sale.total_amount).toFixed(2)}</td>
                  <td><span class="status ${sale.status || 'pending'}">${sale.status || 'N/A'}</span></td>
                  <td>${new Date(sale.created_at).toLocaleDateString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_sales_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Report exported successfully! Open in browser and print as PDF', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [bulkSalesHistory]);

  // Handle export to PDF for proforma invoices
  const handleProformaExportToPDF = useCallback(() => {
    try {
      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Proforma Invoices Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            .date { text-align: center; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { color: #008000; font-weight: bold; }
            .status { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
            .active { background-color: #d4edda; color: #155724; }
            .expired { background-color: #f8d7da; color: #721c24; }
            .converted { background-color: #cce5ff; color: #004085; }
          </style>
        </head>
        <body>
          <h1>Proforma Invoices Report</h1>
          <div class="date">Generated on: ${new Date().toLocaleDateString()}</div>
          <table>
            <thead>
              <tr>
                <th>Proforma ID</th>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${proformaHistory.map(invoice => `
                <tr>
                  <td>${invoice.proforma_id}</td>
                  <td>${invoice.customer_name || invoice.customer?.name || 'N/A'}</td>
                  <td>${invoice.customer_email || invoice.customer?.email || 'N/A'}</td>
                  <td>${invoice.customer_phone || invoice.customer?.phone || 'N/A'}</td>
                  <td class="amount">Ghs ${parseFloat(invoice.grand_total).toFixed(2)}</td>
                  <td><span class="status ${invoice.status || 'active'}">${invoice.status || 'N/A'}</span></td>
                  <td>${new Date(invoice.created_at).toLocaleDateString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proforma_invoices_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Proforma invoices report exported successfully! Open in browser and print as PDF', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting proforma invoices report:', error);
      toast.error('Failed to export proforma invoices report', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [proformaHistory]);

  // Proforma Reports functions
  const fetchProformaReports = useCallback(async () => {
    try {
      setProformaReportsLoading(true);
      
      // Fetch summary
      const summaryResponse = await apiService.getProformaSummary(proformaReportsDateRange);
      if (summaryResponse.success) {
        setProformaReportsData(prev => ({ ...prev, summary: summaryResponse }));
      }
      
      // Fetch analytics
      const analyticsResponse = await apiService.getProformaAnalytics(proformaReportsDateRange);
      if (analyticsResponse.success) {
        setProformaReportsData(prev => ({ ...prev, analytics: analyticsResponse }));
      }
      
      // Fetch invoices
      const invoicesResponse = await apiService.getProformaInvoicesReport(proformaReportsDateRange);
      if (invoicesResponse.success) {
        setProformaReportsData(prev => ({ ...prev, invoices: invoicesResponse }));
      }
      
    } catch (error) {
      console.error('Error fetching proforma reports:', error);
      toast.error('Failed to fetch proforma reports', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setProformaReportsLoading(false);
    }
  }, [proformaReportsDateRange]);

  const handleScrollToTop = useCallback(() => {
    const modalBody = document.querySelector('.proforma-reports-modal-body');
    if (modalBody) {
      modalBody.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleProformaReportsExport = useCallback(async (type) => {
    try {
      if (type === 'excel') {
        const response = await apiService.exportProformaExcel(proformaReportsDateRange);
        if (response.success) {
          // For now, just show success message
          // Later this can be implemented to download actual Excel file
          toast.success('Excel export data prepared successfully!', {
            style: {
              background: '#1f2937',
              color: '#fff',
              border: '1px solid #374151'
            }
          });
        }
      } else if (type === 'pdf') {
        const response = await apiService.exportProformaPdf(proformaReportsDateRange);
        if (response.success) {
          toast.success('PDF export functionality will be implemented soon!', {
            style: {
              background: '#1f2937',
              color: '#fff',
              border: '1px solid #374151'
            }
          });
        }
      }
    } catch (error) {
      console.error('Error exporting proforma reports:', error);
      toast.error(`Failed to export ${type.toUpperCase()}`, {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [proformaReportsDateRange]);

  // Load proforma reports when Proforma Reports modal opens
  useEffect(() => {
    if (activeModal === 'proforma-reports') {
      fetchProformaReports();
    }
  }, [activeModal, fetchProformaReports]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setActiveModal(null);
    resetModalFields();
  }, [resetModalFields]);

  // Receipt Modal Functions
  const handlePrintReceipt = useCallback(() => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">{APP_NAME}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Proforma Invoice Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>Receipt ID:</strong> ${receiptData.proformaId}</span>
            <span><strong>Date:</strong> ${receiptData.date}</span>
            <span><strong>Time:</strong> ${receiptData.time}</span>
            <span><strong>Sales by:</strong> ${receiptData.salesAgent || user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Bill To and Bill From Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Bill To Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Bill To</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.customer.name}</div>
              ${receiptData.customer.id ? `<div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">ID: ${receiptData.customer.id}</div>` : ''}
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.customer.phone}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.customer.email}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.customer.address}</div>
            </div>
          </div>
          
          <!-- Bill From Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Bill From</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Items Purchased</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Qty</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UoM</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Unit Price</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${receiptData.items.map((item, index) => `
                <tr style="${index % 2 === 0 ? 'background: #f8fafc;' : 'background: white;'}">
                  <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.name}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${Number.isInteger(item.quantity) ? item.quantity : item.quantity.toFixed(3)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.uom}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${formatCurrency(item.price)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.amount)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- Payment Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Payment Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Subtotal:</span>
              <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${formatCurrency(receiptData.subtotal)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">VAT (15%):</span>
              <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${formatCurrency(receiptData.vat)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Discount:</span>
              <span style="font-size: 16px; font-weight: bold; color: #dc2626;">-${formatCurrency(receiptData.discount)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Grand Total:</span>
              <span style="font-size: 24px; font-weight: bold; color: #059669;">${formatCurrency(receiptData.grandTotal)}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Proforma Invoice Receipt - ${receiptData.proformaId}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #1f2937;
            }
            .receipt { 
              width: 100%; 
              margin: 0 auto; 
              padding: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show print notification
    toast.success('Proforma invoice receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  }, [receiptData, user, formatCurrency, companySettings]);

  const handleCloseReceipt = useCallback(() => {
    setShowReceiptModal(false);
    setReceiptData(null);
    // Keep the main bulk sale modal open - don't reset or close it
  }, []);

  // Product search functionality
  const searchProducts = useCallback(async (query) => {
    try {
      setSearching(true);
      const data = await apiService.searchProducts(query);
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Get all products functionality
  const getAllProducts = useCallback(async () => {
    try {
      setSearching(true);
      const data = await apiService.getAllProducts();
      setProducts(data.products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.error('Error fetching all products:', error);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      searchProducts(query);
    }, 300),
    [searchProducts]
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value.toUpperCase();
    setSearchQuery(query);
    setSelectedIndex(-1);
    
    // Only show dropdown when actively searching (2+ characters)
    if (query.trim().length >= 2) {
      setShowProductDropdown(true);
      debouncedSearch(query);
    } else {
      setShowProductDropdown(false);
      setProducts([]);
    }
  }, [debouncedSearch]);

  // Handle product selection
  const handleProductSelect = useCallback((product) => {
    setSearchQuery(product.name);
    setSelectedProduct(product);
    setCurrentProductQuantity(product.quantity || product.qty || 0);
    setProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setWorkflowStep('quantity');
    
    // Focus quantity field
    setTimeout(() => {
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) {
        quantityInput.focus();
        quantityInput.select();
        quantityInput.style.border = '2px solid #3b82f6';
        quantityInput.style.backgroundColor = '#dbeafe';
        quantityInput.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
        
        setTimeout(() => {
          quantityInput.style.border = '';
          quantityInput.style.backgroundColor = '';
          quantityInput.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  }, []);

  // Handle quantity change
  const handleQuantityChange = useCallback((productId, quantity) => {
    setSelectedProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, quantity: parseFloat(quantity) || 0, amount: (parseFloat(product.price) * (parseFloat(quantity) || 0)) }
        : product
    ));
  }, []);

  // Handle UOM change
  const handleUOMChange = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.map(product => {
      if (product.id === productId) {
        // Update price based on UOM selection
        let newPrice = product.price;
        if (uom === 'Ctn' && product.selling_price_ctn) {
          newPrice = parseFloat(product.selling_price_ctn);
        } else if (uom === 'Pcs' && product.selling_price_pcs) {
          newPrice = parseFloat(product.selling_price_pcs);
        }
        
        return { 
          ...product, 
          uom,
          price: newPrice,
          amount: newPrice * product.quantity
        };
      }
      return product;
    }));
  }, []);

  // Remove product from table
  const removeProduct = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.filter(product => !(product.id === productId && product.uom === uom)));
    setShowDeleteModal(false);
    setProductToDelete(null);
  }, []);

  // Increase quantity
  const increaseQuantity = useCallback((productId, uom) => {
    
    setSelectedProducts(prev => {
      const updated = prev.map(product => {
        if (product.id === productId && product.uom === uom) {
          // Normalize quantity to whole number if it's very close to one
          let currentQty = product.quantity;
          if (Math.abs(currentQty - Math.round(currentQty)) < 0.01) {
            currentQty = Math.round(currentQty);
          }
          
          // Always increase by 1 (whole number)
          const newQuantity = currentQty + 1;
          const newAmount = product.price * newQuantity;
          return { ...product, quantity: newQuantity, amount: newAmount };
        }
        return product;
      });
      console.log('🔄 New selectedProducts:', updated);
      return updated;
    });
  }, [selectedProducts]);

  // Decrease quantity
  const decreaseQuantity = useCallback((productId, uom) => {
    
    setSelectedProducts(prev => {
      const updated = prev.map(product => {
        if (product.id === productId && product.uom === uom && product.quantity > 0.01) {
          // Normalize quantity to whole number if it's very close to one
          let currentQty = product.quantity;
          if (Math.abs(currentQty - Math.round(currentQty)) < 0.01) {
            currentQty = Math.round(currentQty);
          }
          
          // Always decrease by 1 (whole number), but allow down to 0.01
          const newQuantity = Math.max(0.01, currentQty - 1);
          const newAmount = product.price * newQuantity;
          return { ...product, quantity: newQuantity, amount: newAmount };
        }
        return product;
      });
      console.log('🔄 New selectedProducts:', updated);
      return updated;
    });
  }, [selectedProducts]);

  // Show delete confirmation
  const showDeleteConfirmation = useCallback((product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  }, []);

  // Add product to cart
  const addToCart = useCallback(() => {
    const quantityInput = document.getElementById('quantity-input');
    const uomSelect = document.getElementById('uom-select');
    
    if (quantityInput && uomSelect && selectedProduct) {
      const quantity = parseFloat(quantityInput.value) || 1;
      const uom = uomSelect.value;
      
      // Check if product already exists with the same UoM
      const existingProductIndex = selectedProducts.findIndex(p => p.id === selectedProduct.id && p.uom === uom);
      
      if (existingProductIndex !== -1) {
        // Product exists with same UoM - increase quantity
        setSelectedProducts(prev => prev.map((product, index) => {
          if (index === existingProductIndex) {
            const newQuantity = product.quantity + quantity;
            return {
              ...product,
              quantity: newQuantity,
              amount: product.price * newQuantity
            };
          }
          return product;
        }));
      } else {
        // Product doesn't exist or has different UoM - add as new entry
        setSelectedProducts(prev => [{
          ...selectedProduct,
          quantity,
          uom,
          price: uom === 'Ctn' ? parseFloat(selectedProduct.selling_price_ctn || 0) : parseFloat(selectedProduct.selling_price_pcs || 0),
          amount: (uom === 'Ctn' ? parseFloat(selectedProduct.selling_price_ctn || 0) : parseFloat(selectedProduct.selling_price_pcs || 0)) * quantity
        }, ...prev]);
      }
      
      // Reset workflow
      setSearchQuery('');
      setSelectedProduct(null);
      setProducts([]);
      setShowProductDropdown(false);
      setSelectedIndex(-1);
      setWorkflowStep('search');
      
      // Reset inputs
      quantityInput.value = '1';
      uomSelect.value = 'Ctn';
      
      // Focus back to search
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="Search products"]');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [selectedProduct, selectedProducts]);

  // Handle key navigation in search
  const handleKeyDown = useCallback((e) => {
    if (!showProductDropdown || products.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = selectedIndex < products.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(nextIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${nextIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : products.length - 1;
        setSelectedIndex(prevIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${prevIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < products.length) {
          handleProductSelect(products[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showProductDropdown, products, selectedIndex, handleProductSelect]);

  // Handle search focus
  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      setShowProductDropdown(true);
    }
  }, [searchQuery]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close product dropdown when clicking outside
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
      // Close customer dropdown when clicking outside
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductDropdown, showCustomerDropdown]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <Sidebar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
        />

        {/* Main Content */}
        <div className={`flex-1 flex flex-col min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} transition-all duration-300 ${sidebarVisible ? 'lg:ml-64' : 'lg:ml-16'}`}>
          {/* Navbar */}
          <Navbar 
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarVisible={sidebarVisible}
            setSidebarVisible={setSidebarVisible}
            activeDropdown={activeDropdown}
          />

          {/* Main Content Area */}
          <main className={`flex-1 p-6 overflow-y-auto ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} mt-16 lg:mt-0`}>
            <div className="max-w-7xl mx-auto space-y-8">
            

              {/* Bulk Sales Content */}
              <div className="bg-white rounded-xl p-8 mt-12 shadow-sm border border-gray-100">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">Proforma Invoice Management</h1>
                  <p className="text-xl text-gray-600">Create and manage proforma invoices for customer quotations efficiently</p>
                </div>

                {/* Four Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                  {/* Create Proforma Invoice */}
                  <div className="bg-gradient-to-br from-white to-indigo-50/40 rounded-2xl p-8 border border-indigo-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-indigo-400 via-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                        <FileText size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Create Proforma Invoice</h3>
                        <p className="text-gray-600">Create a new proforma invoice for customer quotations</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('create-proforma')}
                        className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Create New Invoice
                      </button>
                  </div>

                  {/* Proforma Invoice History */}
                  <div className="bg-gradient-to-br from-white to-green-50/40 rounded-2xl p-8 border border-green-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Clock size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Invoice History</h3>
                        <p className="text-gray-600">View and manage all proforma invoices</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('invoice-history')}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        View Invoice History
                      </button>
                  </div>

                  {/* Order Management */}
                  <div className="bg-gradient-to-br from-white to-purple-50/40 rounded-2xl p-8 border border-purple-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg">
                        <ShoppingCart size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Invoice Management</h3>
                        <p className="text-gray-600">Search and manage proforma invoices</p>
                      </div>
                    </div>
                                          <button 
                          onClick={() => setShowInvoiceManagementModal(true)}
                        className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Manage Invoices
                      </button>
                  </div>

                  {/* Bulk Reports */}
                  <div className="bg-gradient-to-br from-white to-orange-50/40 rounded-2xl p-8 border border-orange-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                        <BarChart3 size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Proforma Reports</h3>
                        <p className="text-gray-600">Generate detailed proforma invoice analytics and reports</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('proforma-reports')}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Generate Reports
                      </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  {/* Left side - only show for create-proforma modal */}
                  {activeModal === 'create-proforma' && (
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={handleSaveProforma}
                        disabled={loading}
                        className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl flex items-center space-x-3 transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:transform-none disabled:shadow-none font-semibold text-sm border-0 focus:outline-none focus:ring-4 focus:ring-green-300 focus:ring-opacity-50"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                            <FileText size={14} className="text-white" />
                    </div>
                        )}
                        <span className="font-medium tracking-wide">
                          {loading ? 'Saving...' : 'Save Proforma'}
                        </span>
                      </button>
                      <div className="bg-gray-800 px-4 py-2 rounded-lg font-mono text-sm text-white">
                        {loading ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Generating ID...</span>
                          </div>
                        ) : (
                          <span className="font-bold">
                            {proformaId || `PROF - ${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}001`}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Center - show modal title for other modals */}
                  {activeModal !== 'create-bulk-sale' && (
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                        <BarChart3 size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">
                          {activeModal === 'proforma-reports' ? 'Proforma Reports' : 
                           activeModal === 'invoice-history' ? 'Invoice History' : 
                           'Proforma Invoice'}
                    </h2>
                        <p className="text-white/80 text-sm">
                          {activeModal === 'proforma-reports' ? 'View detailed analytics and reports' : 
                           activeModal === 'invoice-history' ? 'View and manage all proforma invoices' : 
                           'Create and manage proforma invoices'}
                        </p>
                  </div>
                    </div>
                  )}
                  
                  {/* Right side - customer dropdown for create-proforma, close button for all */}
                  <div className="flex items-center space-x-4">
                    {/* Only show customer dropdown for create-proforma modal */}
                    {activeModal === 'create-proforma' && (
                      <div className="relative customer-dropdown-container">
                        <button
                          type="button"
                          onClick={() => {
                            if (!isOneTimeCustomer) {
                              setShowCustomerDropdown(!showCustomerDropdown);
                            }
                          }}
                          disabled={isOneTimeCustomer}
                          className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
                            isOneTimeCustomer 
                              ? 'bg-gray-400 text-gray-600 border-gray-500 cursor-not-allowed' 
                              : 'bg-gray-800 text-white border-gray-700 hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                        >
                          <span className={selectedCustomerName && !isOneTimeCustomer ? 'text-white' : 'text-gray-400'}>
                            {isOneTimeCustomer ? 'One-time Customer' : (selectedCustomerName || 'Choose a customer...')}
                          </span>
                          <svg className={`w-4 h-4 ${isOneTimeCustomer ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Customer Dropdown */}
                        {(() => {
                          const shouldShow = showCustomerDropdown && customers.length > 0 && !isOneTimeCustomer;
                          return shouldShow;
                        })() && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                            {/* Search Input */}
                            <div className="p-2 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                  type="text"
                                  value={customerSearchQuery}
                                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                  placeholder="Search customers..."
                                  className="w-full pl-8 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            {/* Customer Count */}
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                              {filteredCustomers.length} of {customers.length} customers
                            </div>
                            
                            {/* Customer List */}
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer.id)}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm"
                                >
                                  <div className="font-medium text-gray-900">
                                    {customer.name || customer.customer_name}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                                No customers found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                  <button
                    onClick={handleModalClose}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                      Close
                  </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {activeModal === 'create-proforma' && (
                  <div className="flex-1 flex flex-col">
                    {/* Fixed Content - Scrollable on small screens, fixed on large screens */}
                    <div className="p-2 sm:p-3 lg:p-4 space-y-3 sm:space-y-4 lg:space-y-6 flex-shrink-0 overflow-y-auto lg:overflow-visible">
                      {/* Information Sections */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                        {/* Company Information */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-blue-200">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                            <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                              <Package size={14} className="text-white" />
                            </div>
                            <span>Company's Information</span>
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Company (Store) Name</label>
                              <input 
                                type="text" 
                                value={companySettings.company_name}
                                readOnly
                                className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                      </div>
                      <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Sales by / Agent</label>
                              <input 
                                type="text" 
                                value={salesAgent}
                                onChange={(e) => setSalesAgent(e.target.value)}
                                placeholder="Sales agent"
                                className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                      </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Sales Date</label>
                              <div className="relative">
                                <input 
                                  type="text" 
                                  defaultValue={new Date().toLocaleDateString('en-GB')}
                                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8 text-sm"
                                />
                                <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    </div>
                        </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Mode of Sales</label>
                              <select className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                                <option>Cash</option>
                                <option>Credit</option>
                                <option>Bank Transfer</option>
                                <option>Mobile Money</option>
                        </select>
                        </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Remark</label>
                              <input 
                                type="text" 
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                placeholder="Optional note..."
                                className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                      </div>
                    </div>
                        </div>

                        {/* Customer Information */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-2 sm:mb-3">
                            <h3 className="text-sm sm:text-base font-semibold text-gray-900 flex items-center space-x-2">
                              <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                                <User size={14} className="text-white" />
                              </div>
                              <span>Customer's Information</span>
                            </h3>
                            
                            {/* Customer Type Toggle */}
                            <div className="flex items-center space-x-3">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="customerType"
                                  checked={!isOneTimeCustomer}
                                  onChange={() => setIsOneTimeCustomer(false)}
                                  className="text-green-600 focus:ring-green-500"
                                />
                                <span className="text-xs text-gray-700">Existing Customer</span>
                              </label>
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="customerType"
                                  checked={isOneTimeCustomer}
                                  onChange={() => setIsOneTimeCustomer(true)}
                                  className="text-green-600 focus:ring-green-500"
                                />
                                <span className="text-xs text-gray-700">One-time Customer</span>
                              </label>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {/* Customer Name - Only show for one-time customers */}
                            {isOneTimeCustomer && (
                              <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name *</label>
                                <input 
                                  type="text" 
                                  value={oneTimeCustomer.name}
                                  onChange={(e) => setOneTimeCustomer(prev => ({ ...prev, name: e.target.value }))}
                                  placeholder="Enter customer name"
                                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                />
                              </div>
                            )}
                            
                            {/* Customer ID - Only show for existing customers */}
                            {!isOneTimeCustomer && (
                    <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Customer's ID</label>
                                <input 
                                  type="text" 
                                  value={customerInfo.customerId}
                                  placeholder="Customer ID"
                                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                  readOnly
                                />
                    </div>
                            )}
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                              <input 
                                type="tel" 
                                value={isOneTimeCustomer ? oneTimeCustomer.phone : customerInfo.phoneNumber}
                                onChange={isOneTimeCustomer ? (e) => setOneTimeCustomer(prev => ({ ...prev, phone: e.target.value })) : undefined}
                                placeholder="Phone number"
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  isOneTimeCustomer 
                                    ? 'bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500' 
                                    : 'bg-gray-800 text-white border-gray-700'
                                }`}
                                readOnly={!isOneTimeCustomer}
                              />
                    </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Email (optional)</label>
                              <input 
                                type="email" 
                                value={isOneTimeCustomer ? oneTimeCustomer.email : customerInfo.email}
                                onChange={isOneTimeCustomer ? (e) => setOneTimeCustomer(prev => ({ ...prev, email: e.target.value })) : undefined}
                                placeholder="Email address"
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  isOneTimeCustomer 
                                    ? 'bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500' 
                                    : 'bg-gray-800 text-white border-gray-700'
                                }`}
                                readOnly={!isOneTimeCustomer}
                              />
                            </div>
                            
                            {/* Outstanding Amount - Only show for existing customers */}
                            {!isOneTimeCustomer && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Outstanding Amount</label>
                                <input 
                                  type="text" 
                                  value={customerInfo.outstandingAmount}
                                  className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                  readOnly
                                />
                  </div>
                )}

                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Customer's Address</label>
                              <input 
                                type="text" 
                                value={isOneTimeCustomer ? oneTimeCustomer.address : customerInfo.address}
                                onChange={isOneTimeCustomer ? (e) => setOneTimeCustomer(prev => ({ ...prev, address: e.target.value })) : undefined}
                                placeholder="Street, City, Region..."
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                  isOneTimeCustomer 
                                    ? 'bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500' 
                                    : 'bg-gray-800 text-white border-gray-700'
                                  }`}
                                readOnly={!isOneTimeCustomer}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                                              {/* Product Search Section - Fixed */}
                        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200">
                                                  {/* Search Bar */}
                          <div className="bg-white rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200 shadow-sm mb-3 sm:mb-4">
                                                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 items-end">
                            <div className="md:col-span-3">
                                                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                                Search Products
                                                                {selectedProduct && (
                                                                  <span className="ml-2 text-xs text-blue-600 font-medium">
                                                                    → {workflowStep === 'quantity' ? 'Enter Quantity' : workflowStep === 'uom' ? 'Select UOM' : 'Ready'}
                                                                  </span>
                                                                )}
                                                              </label>
                              <div className="relative product-search-container">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input 
                                  type="text" 
                                  value={searchQuery}
                                  onChange={handleSearchChange}
                                  onKeyDown={handleKeyDown}
                                  onFocus={handleSearchFocus}
                                  placeholder="Search products by Name and ProductID..."
                                  className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm uppercase"
                                />
                                <button
                                  onClick={getAllProducts}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-xs font-medium bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors"
                                  title="Show all products"
                                >
                                  All
                        </button>
                                {searching && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                                )}
                                {/* Search Results Dropdown */}
                                {products.length > 0 && showProductDropdown && !isQuantityFocused && !isUomFocused && !selectedProduct && (
                                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                                      {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                    </div>
                                    {products.map((product, index) => (
                                      <div
                                        key={product.id}
                                        data-index={index}
                                        className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                          index === selectedIndex 
                                            ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                            : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handleProductSelect(product)}
                                      >
                                        <div className="flex justify-between items-center">
                                          <div className="flex-1">
                                            <div className="font-medium text-gray-900 text-sm leading-tight">
                                              {product.name}
                      </div>
                    </div>
                                          <div className="flex items-center space-x-3 ml-4">
                                            <div className="text-blue-600 font-semibold text-sm">
                                              ₵{parseFloat(product.selling_price_ctn || 0).toFixed(2)}
                                            </div>
                                            <div className="text-green-600 font-medium text-xs">
                                              ₵{parseFloat(product.selling_price_pcs || 0).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(Qty)</label>
                              <input 
                                id="quantity-input"
                                type="number" 
                                defaultValue="1"
                                min="0"
                                step="0.01"
                                onFocus={(e) => {
                                  setIsQuantityFocused(true);
                                  e.target.select(); // Highlight the text
                                }}
                                onBlur={() => setIsQuantityFocused(false)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setWorkflowStep('uom');
                                    const uomSelect = document.getElementById('uom-select');
                                    if (uomSelect) {
                                      uomSelect.focus();
                                      uomSelect.style.border = '2px solid #3b82f6';
                                      uomSelect.style.backgroundColor = '#dbeafe';
                                      uomSelect.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                                      
                                      setTimeout(() => {
                                        uomSelect.style.border = '';
                                        uomSelect.style.backgroundColor = '';
                                        uomSelect.style.boxShadow = '';
                                      }, 2000);
                                    }
                                  }
                                }}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                          </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(UoM)</label>
                              <select 
                                id="uom-select"
                                tabIndex="0"
                                onFocus={(e) => {
                                  e.target.focus();
                                  setIsUomFocused(true);
                                  e.target.style.border = '2px solid #3b82f6';
                                  e.target.style.backgroundColor = '#dbeafe';
                                }}
                                onBlur={(e) => {
                                  setIsUomFocused(false);
                                  e.target.style.border = '';
                                  e.target.style.backgroundColor = '';
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (selectedProduct && workflowStep === 'uom') {
                                      addToCart();
                                    }
                                  }
                                  if (e.key === ' ') {
                                    e.preventDefault();
                                  }
                                }}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              >
                                <option value="Ctn">Ctn</option>
                                <option value="Pcs">Pcs</option>
                                <option value="Kg">Kg</option>
                                <option value="Ltr">Ltr</option>
                                <option value="Box">Box</option>
                                <option value="Pack">Pack</option>
                              </select>
                        </div>
                          <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Current Qty</label>
                              <input 
                                type="text" 
                                value={currentProductQuantity !== null ? currentProductQuantity : '0'}
                                readOnly
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg text-sm cursor-not-allowed"
                              />
                          </div>
                        </div>
                      </div>

                        {/* Product Table - Fixed Header, Scrollable Body */}
                        <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden flex flex-col h-64 lg:h-80">
                          {/* Table Header - Fixed */}
                          <div className="bg-gray-800 text-white px-4 py-3 grid grid-cols-12 gap-2 font-medium text-sm flex-shrink-0">
                            <div className="col-span-4">PRODUCT</div>
                            <div className="col-span-1 text-right">QTY</div>
                            <div className="col-span-1 text-right">UOM</div>
                            <div className="col-span-2 text-right">PRICE</div>
                            <div className="col-span-2 text-right">AMOUNT</div>
                            <div className="col-span-2 text-right">ACTION</div>
                    </div>
                          
                                                     {/* Table Body - Scrollable */}
                           <div className="flex-1 overflow-y-auto bg-gray-50">
                            {selectedProducts.length === 0 ? (
                              <div className="flex items-center justify-center h-full">
                                <div className="text-center py-6">
                                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-red-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Package size={24} className="lg:w-8 lg:h-8 text-white" />
                                  </div>
                                  <p className="text-gray-600 font-medium text-base lg:text-lg">{APP_NAME}</p>
                                  <p className="text-gray-500 text-sm mt-2">No products added yet</p>
                                  <p className="text-gray-400 text-xs mt-1">Search and add products to create your bulk order</p>
                                </div>
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {selectedProducts.map((product) => (
                                  <div key={product.id} className="px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-100">
                                    <div className="col-span-4">
                                      <span className="font-medium text-gray-900 text-sm block truncate" title={product.name}>{product.name}</span>
                                    </div>
                                    <div className="col-span-1 text-sm font-medium text-gray-900 text-right">
                                      {formatQuantity(product.quantity)}
                                    </div>
                                    <div className="col-span-1 text-sm font-medium text-gray-900 text-right">
                                      {product.uom}
                                    </div>
                                    <div className="col-span-2 text-sm font-medium text-gray-900 text-right">
                                      ₵{parseFloat(product.price).toFixed(2)}
                                    </div>
                                    <div className="col-span-2 text-sm font-medium text-gray-900 text-right">
                                      ₵{product.amount.toFixed(2)}
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end space-x-2">
                                      <button
                                        onClick={() => decreaseQuantity(product.id, product.uom)}
                                        disabled={product.quantity <= 0.01}
                                        className="w-6 h-6 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                        title="Decrease quantity"
                                      >
                                        -
                      </button>
                                      <button
                                        onClick={() => increaseQuantity(product.id, product.uom)}
                                        className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                        title="Increase quantity"
                                      >
                                        +
                                      </button>
                                      <button
                                        onClick={() => showDeleteConfirmation(product)}
                                        className="w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded flex items-center justify-center text-xs transition-colors"
                                        title="Delete item"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                      </button>
                    </div>
                                  </div>
                                ))}
                  </div>
                )}
                          </div>
                        </div>
                      </div>

                                              {/* Summary Section - Fixed */}
                                                <div className="bg-gray-800 text-white rounded-xl p-2 sm:p-3 lg:p-4 flex-shrink-0">
                          <div className="flex flex-col lg:flex-row justify-end space-y-2 sm:space-y-4 lg:space-y-0 lg:space-x-8 text-sm sm:text-base lg:text-lg">
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              <span className="text-gray-300">Discount:</span>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-300">₵</span>
                                <input
                                  type="number"
                                  value={discount}
                                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                  className="w-20 sm:w-24 bg-gray-700 text-white border border-gray-600 rounded px-6 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              <span className="text-gray-300">Subtotal:</span>
                              <span className="font-semibold">₵{subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              <span className="text-gray-300">VAT (15%):</span>
                              <span className="font-semibold">₵{vat.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                              <div className="flex items-center space-x-2 sm:space-x-4">
                                <span className="text-gray-300">Discount:</span>
                                <span className="font-semibold text-red-400">-₵{discount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              <span className="text-gray-300">Grand Total:</span>
                              <span className="font-bold text-green-400 text-xl">₵{grandTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                    </div>
                  </div>
                )}



                {activeModal === 'order-management' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Order Status</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                          <option>All Orders</option>
                          <option>Pending</option>
                          <option>Processing</option>
                          <option>Completed</option>
                          <option>Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Order Number</label>
                        <input type="text" placeholder="Enter order number..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Order Details</h3>
                      <div className="bg-white rounded-lg p-4 border">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">Order #BO001</span>
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Completed</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>Customer: Wholesale Customer A</p>
                            <p>Total: ₵2,500.00</p>
                            <p>Items: 15 products</p>
                            <p>Date: 2024-01-15</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Update Status
                      </button>
                      <button onClick={handleModalClose} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300">
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {activeModal === 'bulk-reports' && (
                  <div className="flex flex-col h-full">
                    {/* Fixed Header Section */}
                    <div className="flex-shrink-0 space-y-6">
                      {/* Date Range Filter */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex flex-col sm:flex-row gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.startDate}
                              onChange={(e) => handleReportsDateChange('startDate', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.endDate}
                              onChange={(e) => handleReportsDateChange('endDate', e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={handleApplyReportsFilter}
                              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Apply Filter
                            </button>
                            <button 
                              onClick={handleTodayReportsFilter}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Today
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Report Tabs */}
                      <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setActiveReportTab('dashboard')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'dashboard'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={() => setActiveReportTab('sales')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'sales'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Sales Report
                          </button>
                          <button
                            onClick={() => setActiveReportTab('products')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'products'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Products
                          </button>
                          <button
                            onClick={() => setActiveReportTab('customers')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'customers'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Customers
                          </button>
                        </nav>
                      </div>
                    </div>

                    {/* Scrollable Content Section */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {/* Loading State */}
                      {reportsLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                          <span className="ml-2 text-gray-600">Loading reports...</span>
                        </div>
                      )}

                      {/* Dashboard Tab */}
                      {activeReportTab === 'dashboard' && !reportsLoading && reportsData.dashboardStats && (
                  <div className="space-y-6">
                          {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                              <h3 className="font-medium text-emerald-900">Total Bulk Orders</h3>
                              <p className="text-2xl font-bold text-emerald-600">
                                {reportsData.dashboardStats.total_orders || 0}
                              </p>
                              <p className="text-sm text-emerald-600">
                                {reportsData.dashboardStats.total_transactions || 0} transactions
                              </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <h3 className="font-medium text-blue-900">Total Revenue</h3>
                              <p className="text-2xl font-bold text-blue-600">
                                ₵{parseFloat(reportsData.dashboardStats.total_revenue || 0).toFixed(2)}
                              </p>
                              <p className="text-sm text-blue-600">
                                {reportsData.dashboardStats.total_transactions || 0} orders
                              </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <h3 className="font-medium text-orange-900">Average Order</h3>
                              <p className="text-2xl font-bold text-orange-600">
                                ₵{parseFloat(reportsData.dashboardStats.average_order || 0).toFixed(2)}
                              </p>
                              <p className="text-sm text-orange-600">
                                Per order value
                              </p>
                      </div>
                    </div>

                          {/* Top Customers */}
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Customers</h3>
                        </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.dashboardStats.top_customers?.map((customer, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {customer.customer_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {customer.order_count} orders
                                      </p>
                        </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        ₵{parseFloat(customer.total_amount).toFixed(2)}
                                      </p>
                                      <p className="text-sm text-gray-500">Total Revenue</p>
                      </div>
                    </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Sales Report Tab */}
                      {activeReportTab === 'sales' && !reportsLoading && reportsData.salesReport && (
                        <div className="space-y-6">
                          {/* Sales Summary */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="font-medium text-gray-900 mb-3">Sales Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-emerald-600">
                                  ₵{parseFloat(reportsData.salesReport.summary?.total_sales || 0).toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600">Total Sales</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">
                                  {reportsData.salesReport.summary?.total_transactions || 0}
                                </p>
                                <p className="text-sm text-gray-600">Total Orders</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-orange-600">
                                  ₵{parseFloat(reportsData.salesReport.summary?.average_sale || 0).toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600">Average Sale</p>
                              </div>
                            </div>
                          </div>

                          {/* Sales Table */}
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Recent Sales</h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {reportsData.salesReport.recent_sales?.map((sale, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {sale.bulk_sale_id}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {sale.customer_name}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(sale.created_at).toLocaleDateString()}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                        ₵{parseFloat(sale.total_amount).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Products Report Tab */}
                      {activeReportTab === 'products' && !reportsLoading && reportsData.productsReport && (
                        <div className="space-y-6">
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Products by Revenue</h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.productsReport.top_products?.map((product, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {product.product_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {product.order_count} orders
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        ₵{parseFloat(product.total_amount).toFixed(2)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        Qty: {product.total_quantity}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Customers Report Tab */}
                      {activeReportTab === 'customers' && !reportsLoading && reportsData.customersReport && (
                        <div className="space-y-6">
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Customers by Revenue</h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.customersReport.top_customers?.map((customer, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {customer.customer_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {customer.order_count} orders
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        ₵{parseFloat(customer.total_amount).toFixed(2)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        Avg: ₵{parseFloat(customer.average_order).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* No Data State */}
                      {!reportsLoading && !reportsData.dashboardStats && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No reports data available. Please select a date range and apply filters.</p>
                        </div>
                      )}
                    </div>

                    {/* Export Buttons */}
                    <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Export PDF
                      </button>
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Export Excel
                      </button>
                      <button onClick={handleModalClose} className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300">
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Invoice History Modal */}
      {activeModal === 'invoice-history' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col min-h-[80vh] max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Clock size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white tracking-tight">Invoice History</h2>
                      <p className="text-white/80 text-sm">View and manage all proforma invoices</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input 
                        type="text" 
                        placeholder="Search customer name..."
                        value={proformaHistorySearchTerm || ''}
                        onChange={(e) => handleProformaHistorySearch(e.target.value)}
                        className="w-64 border border-white/30 rounded-lg px-4 py-2 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {proformaHistoryLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white/70"></div>
                        ) : (
                          <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={handleProformaExportToExcel} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">Excel</span>
                    </button>
                    <button 
                      onClick={handleProformaExportToPDF} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                    <button
                      onClick={handleModalClose}
                      className="bg-red-500/90 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50/30">
                <div className="flex-1 flex flex-col">
                  
                  {/* Table Section */}
                  <div className="flex-1 overflow-hidden p-4">
                    <div className="h-full bg-white/90 backdrop-blur-sm rounded-2xl shadow-inner border border-gray-200/30 overflow-hidden transform translate-y-1">
                      <div className="overflow-x-auto overflow-y-auto h-full" style={{ minHeight: '400px', maxHeight: 'calc(100vh - 350px)' }}>
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Proforma ID</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Customer Name</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Email</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Phone Number</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Amount</th>
                              <th className="px-6 py-4 text-center text-xs uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {proformaHistoryLoading ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mb-2"></div>
                                    <span className="text-gray-600 text-sm">Searching...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : proformaHistory.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-600 font-medium">No proforma invoices found</span>
                                    <span className="text-gray-500 text-sm">Try adjusting your search criteria</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              proformaHistory.map((invoice, index) => (
                                <tr key={invoice.id} className={`hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                      <span className="font-semibold text-gray-900">{invoice.proforma_id}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-white text-xs font-bold">
                                          {(invoice.customer_name || invoice.customer?.name || 'N/A').charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="font-medium text-gray-900">{invoice.customer_name || invoice.customer?.name || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{invoice.customer_email || invoice.customer?.email || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{invoice.customer_phone || invoice.customer?.phone || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200">
                                      Ghs {parseFloat(invoice.grand_total).toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <div className="flex items-center justify-center space-x-2">
                                      <button 
                                        onClick={() => handlePrintExistingProformaReceipt(invoice)}
                                        className="w-9 h-9 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Print Receipt"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                      <button 
                                        onClick={() => handleEditProformaInvoice(invoice)}
                                        className="w-9 h-9 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Edit Invoice"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Sales History Modal */}
      {activeModal === 'bulk-sales-history' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col min-h-[80vh] max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Clock size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white tracking-tight">Bulk Sales History</h2>
                      <p className="text-white/80 text-sm">View and manage all bulk sales transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input 
                        type="text" 
                        placeholder="Search customer name..."
                        value={bulkSalesHistorySearchTerm || ''}
                        onChange={(e) => handleBulkSalesHistorySearch(e.target.value)}
                        className="w-64 border border-white/30 rounded-lg px-4 py-2 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {bulkSalesHistoryLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white/70"></div>
                        ) : (
                          <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={handleExportToExcel} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">Excel</span>
                    </button>
                    <button 
                      onClick={handleExportToPDF} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                    <button
                      onClick={handleModalClose}
                      className="bg-red-500/90 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50/30">
                <div className="flex-1 flex flex-col">
                  
                  {/* Table Section */}
                  <div className="flex-1 overflow-hidden p-4">
                    <div className="h-full bg-white/90 backdrop-blur-sm rounded-2xl shadow-inner border border-gray-200/30 overflow-hidden transform translate-y-1">
                      <div className="overflow-x-auto overflow-y-auto h-full" style={{ minHeight: '400px', maxHeight: 'calc(100vh - 350px)' }}>
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">BulkSale ID</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Customer Name</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Email</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Phone Number</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Amount</th>
                              <th className="px-6 py-4 text-center text-xs uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {bulkSalesHistoryLoading ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mb-2"></div>
                                    <span className="text-gray-600 text-sm">Searching...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : bulkSalesHistory.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-600 font-medium">No bulk sales found</span>
                                    <span className="text-gray-500 text-sm">Try adjusting your search criteria</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              bulkSalesHistory.map((sale, index) => (
                                <tr key={sale.id} className={`hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                      <span className="font-semibold text-gray-900">{sale.bulk_sale_id}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-white text-xs font-bold">
                                          {(sale.customer_name || 'N/A').charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="font-medium text-gray-900">{sale.customer_name || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{sale.customer_email || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{sale.customer_phone || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200">
                                      Ghs {parseFloat(sale.total_amount).toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <div className="flex items-center justify-center space-x-2">
                                      <button 
                                        onClick={() => handlePrintExistingReceipt(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Print Receipt"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                      <button 
                                        onClick={() => handleEditSale(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Edit Sale"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button 
                                        onClick={() => handleDeleteSale(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Delete Sale"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-t border-blue-200/50 shadow-inner">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium text-blue-800">Total Records:</span> {bulkSalesHistoryTotal} bulk sales
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium text-blue-800">Scroll</span> to view all records
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Edit Sale Modal - Using Exact Create Bulk Sales Structure */}
      {showEditModal && selectedSale && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={handleEditSubmit}
                      disabled={editLoading}
                      className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    >
                      <FileText size={16} />
                      <span>{editLoading ? 'Updating...' : 'Update'}</span>
                    </button>
                    <div className="bg-gray-800 px-4 py-2 rounded-lg font-mono text-sm text-white">
                      <span className="font-bold">{selectedSale.bulk_sale_id}</span>
                    </div>
                    </div>
                  <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  </div>
                </div>
              </div>

              {/* Modal Body - Exact same structure as create modal */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 flex flex-col">
                  {/* Fixed Content - Scrollable on small screens, fixed on large screens */}
                  <div className="p-2 sm:p-3 lg:p-4 space-y-3 sm:space-y-4 lg:space-y-6 flex-shrink-0 overflow-y-auto lg:overflow-visible">
                    {/* Information Sections */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {/* Company Information */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-blue-200">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                          <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                            <Package size={14} className="text-white" />
                          </div>
                          <span>Company's Information</span>
                        </h3>
                                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Company (Store) Name</label>
                             <input 
                               type="text" 
                               value={editFormData.company_name}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, company_name: e.target.value }))}
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Sales by / Agent</label>
                             <input 
                               type="text" 
                               value={editFormData.sales_agent}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, sales_agent: e.target.value }))}
                               placeholder="Sales agent"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Sales Date</label>
                             <div className="relative">
                               <input 
                                 type="text" 
                                 value={editFormData.sales_date}
                                 onChange={(e) => setEditFormData(prev => ({ ...prev, sales_date: e.target.value }))}
                                 className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8 text-sm"
                               />
                               <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                             </div>
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Mode of Sales</label>
                             <select 
                               value={editFormData.mode_of_sales}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, mode_of_sales: e.target.value }))}
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             >
                               <option value="Cash">Cash</option>
                               <option value="Credit">Credit</option>
                               <option value="Bank Transfer">Bank Transfer</option>
                               <option value="Mobile Money">Mobile Money</option>
                             </select>
                           </div>
                           <div className="sm:col-span-2">
                             <label className="block text-xs font-medium text-gray-700 mb-1">Remark</label>
                             <input 
                               type="text" 
                               value={editFormData.remark}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, remark: e.target.value }))}
                               placeholder="Optional note..."
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                         </div>
                      </div>

                      {/* Customer Information */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-green-200">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                          <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                            <User size={14} className="text-white" />
                          </div>
                          <span>Customer's Information</span>
                        </h3>
                                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                           {/* Customer ID - Only show for existing customers */}
                           {selectedSale.customer_id && (
                             <div>
                               <label className="block text-xs font-medium text-gray-700 mb-1">Customer's ID</label>
                               <input 
                                 type="text" 
                                 value={selectedSale.customer?.customerID || selectedSale.customer_id}
                                 placeholder="Customer ID"
                                 className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                 readOnly
                               />
                             </div>
                           )}
                           

                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
                             <input 
                               type="text" 
                               value={editFormData.customer_name}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                               placeholder="Customer name"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                             <input 
                               type="tel" 
                               value={editFormData.customer_phone}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                               placeholder="Phone number"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Email (optional)</label>
                             <input 
                               type="email" 
                               value={editFormData.customer_email}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                               placeholder="Email address"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div className="sm:col-span-2">
                             <label className="block text-xs font-medium text-gray-700 mb-1">Customer's Address</label>
                             <input 
                               type="text" 
                               value={editFormData.customer_address}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                               placeholder="Street, City, Region..."
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                         </div>
                      </div>
                    </div>

                    {/* Product Search Section - EXACT same as create modal */}
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200">
                      {/* Search Bar */}
                      <div className="bg-white rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200 shadow-sm mb-3 sm:mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 items-end">
                          <div className="md:col-span-3">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                              Search Products
                              {selectedProduct && (
                                <span className="ml-2 text-xs text-blue-600 font-medium">
                                  → {workflowStep === 'quantity' ? 'Enter Quantity' : workflowStep === 'uom' ? 'Select UOM' : 'Ready'}
                                </span>
                              )}
                            </label>
                            <div className="relative product-search-container">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                              <input 
                                type="text" 
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown}
                                onFocus={handleSearchFocus}
                                placeholder="Search products by Name and ProductID..."
                                className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm uppercase"
                              />
                              <button
                                onClick={getAllProducts}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-xs font-medium bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors"
                                title="Show all products"
                              >
                                All
                              </button>
                              {searching && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                              {/* Search Results Dropdown */}
                              {products.length > 0 && showProductDropdown && !isQuantityFocused && !isUomFocused && !selectedProduct && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                                    {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                                  </div>
                                  {products.map((product, index) => (
                                    <div
                                      key={product.id}
                                      data-index={index}
                                      className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                        index === selectedIndex 
                                          ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                          : 'hover:bg-gray-50'
                                      }`}
                                      onClick={() => handleProductSelect(product)}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="font-medium text-gray-900 text-sm leading-tight">
                                            {product.name}
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3 ml-4">
                                          <div className="text-blue-600 font-semibold text-sm">
                                            ₵{parseFloat(product.selling_price_ctn || 0).toFixed(2)}
                                          </div>
                                          <div className="text-green-600 font-medium text-xs">
                                            ₵{parseFloat(product.selling_price_pcs || 0).toFixed(2)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(Qty)</label>
                            <input 
                              id="quantity-input"
                              type="number" 
                              defaultValue="1"
                              min="0"
                              step="0.01"
                              onFocus={(e) => {
                                setIsQuantityFocused(true);
                                e.target.select(); // Highlight the text
                              }}
                              onBlur={() => setIsQuantityFocused(false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  setWorkflowStep('uom');
                                  const uomSelect = document.getElementById('uom-select');
                                  if (uomSelect) {
                                    uomSelect.focus();
                                    uomSelect.style.border = '2px solid #3b82f6';
                                    uomSelect.style.backgroundColor = '#dbeafe';
                                    uomSelect.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                                    
                                    setTimeout(() => {
                                      uomSelect.style.border = '';
                                      uomSelect.style.backgroundColor = '';
                                      uomSelect.style.boxShadow = '';
                                    }, 2000);
                                  }
                                }
                              }}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(UoM)</label>
                            <select 
                              id="uom-select"
                              tabIndex="0"
                              onFocus={(e) => {
                                e.target.focus();
                                setIsUomFocused(true);
                                e.target.style.border = '2px solid #3b82f6';
                                e.target.style.backgroundColor = '#dbeafe';
                              }}
                              onBlur={(e) => {
                                setIsUomFocused(false);
                                e.target.style.border = '';
                                e.target.style.backgroundColor = '';
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (selectedProduct && workflowStep === 'uom') {
                                    addToCart();
                                  }
                                }
                                if (e.key === ' ') {
                                  e.preventDefault();
                                }
                              }}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                            >
                              <option value="Ctn">Ctn</option>
                              <option value="Pcs">Pcs</option>
                              <option value="Kg">Kg</option>
                              <option value="Ltr">Ltr</option>
                              <option value="Box">Box</option>
                              <option value="Pack">Pack</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Current Qty</label>
                            <input 
                              type="text" 
                              value={currentProductQuantity !== null ? currentProductQuantity : '0'}
                              readOnly
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg text-sm cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Product Table - Fixed Header, Scrollable Body */}
                      <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden flex flex-col h-64 lg:h-80">
                        {/* Table Header - Fixed */}
                        <div className="bg-gray-800 text-white px-4 py-3 grid grid-cols-12 gap-2 font-medium text-sm flex-shrink-0">
                          <div className="col-span-4">PRODUCT</div>
                          <div className="col-span-1 text-right">QTY</div>
                          <div className="col-span-1 text-right">UOM</div>
                          <div className="col-span-2 text-right">PRICE</div>
                          <div className="col-span-2 text-right">AMOUNT</div>
                          <div className="col-span-2 text-right">ACTION</div>
                        </div>
                        
                        {/* Table Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto bg-gray-50">
                          {selectedProducts.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                              No products added to cart yet
                            </div>
                          ) : (
                            selectedProducts.map((product, index) => (
                              <div key={`${product.id}-${index}`} className="px-4 py-3 grid grid-cols-12 gap-2 items-center border-b border-gray-200 last:border-b-0 hover:bg-gray-100 transition-colors">
                                <div className="col-span-4">
                                  <div className="text-sm font-medium text-gray-900 lg:whitespace-normal sm:truncate">
                                    {product.name}
                                  </div>
                                </div>
                                                                    <div className="col-span-1 text-right text-sm text-gray-900">
                                      {formatQuantity(product.quantity)}
                                    </div>
                                <div className="col-span-1 text-right text-sm text-gray-900">
                                  {product.uom}
                                </div>
                                <div className="col-span-2 text-right text-sm text-gray-900">
                                  ₵{parseFloat(product.price).toFixed(2)}
                                </div>
                                <div className="col-span-2 text-right text-sm font-medium text-gray-900">
                                  ₵{parseFloat(product.amount).toFixed(2)}
                                </div>
                                <div className="col-span-2 flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => decreaseQuantity(product.id, product.uom)}
                                    disabled={product.quantity <= 0.01}
                                    className="w-6 h-6 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                    title="Decrease quantity"
                                  >
                                    -
                                  </button>
                                  <button
                                    onClick={() => increaseQuantity(product.id, product.uom)}
                                    className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                    title="Increase quantity"
                                  >
                                    +
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(product)}
                                    className="w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded flex items-center justify-center text-xs transition-colors"
                                    title="Delete item"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Summary Section */}
                    <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-4 border border-slate-600">
                      <div className="flex items-center justify-between space-x-6">
                        {/* Discount */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Discount:</span>
                             <div className="flex items-center space-x-2">
                               <input
                                 type="number"
                                 value={editFormData.discount}
                                 onChange={(e) => setEditFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                              className="w-20 px-3 py-2 bg-slate-600 text-white border border-slate-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-right"
                              placeholder="0"
                                 min="0"
                                 step="0.01"
                               />
                            <span className="text-gray-300 text-sm">₵</span>
                             </div>
                           </div>
                           
                        {/* Subtotal */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Subtotal:</span>
                          <span className="text-white font-bold">
                            ₵{selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0).toFixed(2)}
                               </span>
                             </div>

                        {/* VAT */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">VAT (15%):</span>
                          <span className="text-white font-bold">
                            ₵{(selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0) * 0.15).toFixed(2)}
                          </span>
                           </div>

                        {/* Grand Total */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Grand Total:</span>
                          <span className="text-green-400 font-bold text-lg">
                            ₵{((selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0) * 1.15) - (parseFloat(editFormData.discount) || 0)).toFixed(2)}
                          </span>
                         </div>
                        
                        {/* Update Button */}
                          <button
                            onClick={handleEditSubmit}
                            disabled={editLoading}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {editLoading ? (
                              <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              <span>Updating...</span>
                              </>
                            ) : (
                              <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Update Sale</span>
                              </>
                            )}
                          </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proforma Reports Modal */}
      {activeModal === 'proforma-reports' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col min-h-[80vh] max-h-[98vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                      <BarChart3 size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white tracking-tight">Proforma Reports</h2>
                      <p className="text-white/80 text-sm">View detailed analytics and reports</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Date Range Picker */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="date"
                        value={proformaReportsDateRange.start_date}
                        onChange={(e) => setProformaReportsDateRange(prev => ({ ...prev, start_date: e.target.value }))}
                        className="px-3 py-2 border border-white/30 rounded-lg bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:border-white/50 text-sm"
                      />
                      <span className="text-white/80">to</span>
                      <input
                        type="date"
                        value={proformaReportsDateRange.end_date}
                        onChange={(e) => setProformaReportsDateRange(prev => ({ ...prev, end_date: e.target.value }))}
                        className="px-3 py-2 border border-white/30 rounded-lg bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:border-white/50 text-sm"
                      />
                    </div>
                    
                    {/* Refresh Button */}
                    <button
                      onClick={fetchProformaReports}
                      disabled={proformaReportsLoading}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30 disabled:opacity-50"
                    >
                      <RefreshCw size={16} className={proformaReportsLoading ? 'animate-spin' : ''} />
                      <span className="text-sm font-medium">Refresh</span>
                    </button>
                    
                    {/* Export Buttons */}
                    <button
                      onClick={() => handleProformaReportsExport('excel')}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">Excel</span>
                    </button>
                    <button
                      onClick={() => handleProformaReportsExport('pdf')}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                    
                    <button
                      onClick={handleModalClose}
                      className="bg-red-500/90 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-y-auto bg-gradient-to-br from-gray-50 to-orange-50/30 scroll-smooth proforma-reports-modal-body" onScroll={(e) => setShowScrollToTop(e.target.scrollTop > 100)}>
                <div className="flex-1 flex flex-col p-6 pb-8">
                  
                  {/* Loading State */}
                  {proformaReportsLoading && (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-200 border-t-orange-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading proforma reports...</p>
                      </div>
                    </div>
                  )}

                  {/* Reports Content */}
                  {!proformaReportsLoading && (
                    <div className="space-y-6">
                      
                      {/* Summary Cards */}
                      {proformaReportsData.summary && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                                <p className="text-2xl font-bold text-gray-900">{proformaReportsData.summary.summary.total_invoices}</p>
                              </div>
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <FileText size={20} className="text-blue-600" />
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                                <p className="text-2xl font-bold text-green-600">₵{parseFloat(proformaReportsData.summary.summary.total_amount || 0).toFixed(2)}</p>
                              </div>
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <DollarSign size={20} className="text-green-600" />
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Active Invoices</p>
                                <p className="text-2xl font-bold text-orange-600">{proformaReportsData.summary.summary.active_invoices}</p>
                              </div>
                              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Activity size={20} className="text-orange-600" />
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                                <p className="text-2xl font-bold text-purple-600">
                                  {proformaReportsData.summary.summary.total_invoices > 0 
                                    ? Math.round((proformaReportsData.summary.summary.converted_invoices / proformaReportsData.summary.summary.total_invoices) * 100)
                                    : 0}%
                                </p>
                              </div>
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <TrendingUp size={20} className="text-purple-600" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Analytics Section */}
                      {proformaReportsData.analytics && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Top Customers */}
                          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers</h3>
                            <div className="space-y-3">
                              {proformaReportsData.analytics.top_customers?.map((customer, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{customer.customer_name}</p>
                                      <p className="text-sm text-gray-600">{customer.invoice_count} invoices</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-green-600">₵{parseFloat(customer.total_amount).toFixed(2)}</p>
                                    <p className="text-sm text-gray-600">Avg: ₵{parseFloat(customer.average_amount).toFixed(2)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Top Products */}
                          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h3>
                            <div className="space-y-3">
                              {proformaReportsData.analytics.top_products?.map((product, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                      <span className="text-sm font-semibold text-green-600">{index + 1}</span>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{product.product_name}</p>
                                      <p className="text-sm text-gray-600">{product.invoice_count} invoices</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-blue-600">{product.total_quantity} units</p>
                                    <p className="text-sm text-gray-600">₵{parseFloat(product.total_amount).toFixed(2)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Conversion Analysis */}
                      {proformaReportsData.analytics && (
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Analysis</h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                              <p className="text-2xl font-bold text-blue-600">{proformaReportsData.analytics.conversion_analysis.conversion_rate}%</p>
                              <p className="text-sm text-gray-600">Conversion Rate</p>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                              <p className="text-2xl font-bold text-green-600">{proformaReportsData.analytics.conversion_analysis.average_conversion_time} days</p>
                              <p className="text-sm text-gray-600">Avg Conversion Time</p>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                              <p className="text-2xl font-bold text-purple-600">{proformaReportsData.analytics.conversion_analysis.converted_invoices}</p>
                              <p className="text-sm text-gray-600">Converted Invoices</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Recent Invoices */}
                      {proformaReportsData.invoices && (
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Invoices</h3>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {proformaReportsData.invoices.invoices?.slice(0, 10).map((invoice, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium text-gray-900">{invoice.proforma_id}</td>
                                    <td className="px-4 py-2 text-gray-900">{invoice.customer_name || invoice.customer?.name || 'N/A'}</td>
                                    <td className="px-4 py-2 font-semibold text-green-600">₵{parseFloat(invoice.grand_total).toFixed(2)}</td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(invoice.status)}`}>
                                        {invoice.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-600">{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                                             )}
                     </div>
                   )}
                   
                   {/* Scroll Indicator */}
                   <div className="text-center py-4 text-gray-400 text-sm">
                     <div className="flex items-center justify-center space-x-2">
                       <div className="w-1 h-1 bg-gray-300 rounded-full animate-pulse"></div>
                       <div className="w-1 h-1 bg-gray-300 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                       <div className="w-1 h-1 bg-gray-300 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                     </div>
                     <p className="mt-2 text-xs">Scroll for more content</p>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           
           {/* Scroll to Top Button */}
           {showScrollToTop && (
             <button
               onClick={handleScrollToTop}
               className="fixed bottom-8 right-8 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
               title="Scroll to top"
             >
               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
               </svg>
             </button>
           )}
         </div>
       )}

      {/* Delete Confirmation Modal */}
      {showBulkSaleDeleteModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Delete Bulk Sale</h2>
                    <p className="text-white/80 text-sm">{selectedSale.bulk_sale_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBulkSaleDeleteModal(false)}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure?</h3>
                  <p className="text-gray-600">
                    You are about to delete bulk sale <span className="font-semibold">{selectedSale.bulk_sale_id}</span>.
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      // Remove the sale from the list
                      setBulkSalesHistory(prev => prev.filter(s => s.id !== selectedSale.id));
                      setBulkSalesHistoryTotal(prev => prev - 1);
                      setShowBulkSaleDeleteModal(false);
                      setSelectedSale(null);
                      
                      toast.success(`Bulk sale ${selectedSale.bulk_sale_id} deleted successfully`, {
                        style: {
                          background: '#1f2937',
                          color: '#fff',
                          border: '1px solid #374151'
                        }
                      });
                    }}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowBulkSaleDeleteModal(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Item</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{productToDelete.name}</span> from the cart?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setProductToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => removeProduct(productToDelete.id, productToDelete.uom)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Proforma Invoice Receipt</h2>
                      <p className="text-white/80 text-sm">ID: {receiptData.proformaId}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handlePrintReceipt}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print</span>
                    </button>
                    <button
                      onClick={handleCloseReceipt}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 max-h-[calc(95vh-140px)] overflow-y-auto">
                <div className="space-y-8">
                  {/* Header Section */}
                  <div className="space-y-6">
                    {/* Receipt Header */}
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">{APP_NAME}</h1>
                      <p className="text-gray-600 font-medium">Proforma Invoice Receipt</p>
                      <div className="flex justify-between items-center text-sm text-gray-600 mt-4">
                        <span><strong>Receipt ID:</strong> {receiptData.proformaId}</span>
                        <span><strong>Date:</strong> {receiptData.date}</span>
                        <span><strong>Time:</strong> {receiptData.time}</span>
                        <span><strong>Sales by:</strong> {receiptData.salesAgent || user?.name || 'Agent'}</span>
                      </div>
                    </div>

                    {/* Bill To and Bill From Section */}
                    <div className="flex gap-6">
                      {/* Bill To Section (Left) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Bill To</h3>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{receiptData.customer.name}</div>
                          {receiptData.customer.id && (
                            <div className="text-gray-600 flex items-center space-x-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                              </svg>
                              <span>ID: {receiptData.customer.id}</span>
                            </div>
                          )}
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{receiptData.customer.phone}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>{receiptData.customer.email}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{receiptData.customer.address}</span>
                          </div>
                        </div>
                      </div>

                      {/* Bill From Section (Right) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Bill From</h3>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{companySettings.company_name}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{companySettings.company_address}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{companySettings.company_phone}</span>
                          </div>
                          {companySettings.tax_number && (
                            <div className="text-gray-600 flex items-center space-x-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                              </svg>
                              <span>Tax ID: {companySettings.tax_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>Items Purchased</span>
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-100 to-gray-50">
                            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-l-lg">Product</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Qty</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">UoM</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700">Unit Price</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700 rounded-r-lg">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptData.items.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-800 font-medium">{item.name}</td>
                              <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800">{formatQuantity(item.quantity)}</td>
                              <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800">{item.uom}</td>
                              <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800">{formatCurrency(item.price)}</td>
                              <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800 font-semibold">{formatCurrency(item.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>Payment Summary</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Subtotal:</span>
                        <span className="font-semibold text-gray-800 text-lg">{formatCurrency(receiptData.subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">VAT (15%):</span>
                        <span className="font-semibold text-gray-800 text-lg">{formatCurrency(receiptData.vat)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Discount:</span>
                        <span className="font-semibold text-red-600 text-lg">-{formatCurrency(receiptData.discount)}</span>
                      </div>
                      <div className="border-t-2 border-gray-300 pt-4 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-xl font-bold text-gray-800">Grand Total:</span>
                          <span className="text-2xl font-bold text-green-600">{formatCurrency(receiptData.grandTotal)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 mt-8">
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Receipt generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Management Modal */}
      {showInvoiceManagementModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Settings className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Invoice Management</h2>
                      <p className="text-white/80 text-sm">Manage proforma invoice status and actions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowInvoiceManagementModal(false);
                      resetInvoiceManagementModal();
                    }}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 max-h-[calc(95vh-120px)] overflow-y-auto">
                <div className="space-y-6">

                  {/* Filters Section */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-4">Filters</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Date Range Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={proformaHistoryStartDate}
                          onChange={(e) => setProformaHistoryStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="date"
                          value={proformaHistoryEndDate}
                          onChange={(e) => setProformaHistoryEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={proformaHistoryStatus}
                          onChange={(e) => setProformaHistoryStatus(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        >
                          <option value="all">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="converted">Converted</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      {/* Apply Filters Button */}
                      <div className="flex items-end space-x-2">
                        <button
                          onClick={() => fetchProformaHistory()}
                          disabled={proformaHistoryLoading}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                        >
                          {proformaHistoryLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Filter size={16} />
                          )}
                          <span>{proformaHistoryLoading ? 'Applying...' : 'Apply Filters'}</span>
                        </button>
                        <button
                          onClick={() => {
                            // Reset everything using the reset function
                            resetInvoiceManagementModal();
                            // Show success message
                            toast.success('All filters and selections cleared', {
                              style: {
                                background: '#1f2937',
                                color: '#fff',
                                border: '1px solid #374151'
                              }
                            });
                            // Refresh data with cleared filters
                            fetchProformaHistory();
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                          title="Clear all filters and selections"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Results Summary */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-blue-800">Results:</span>
                          <span className="text-lg font-bold text-blue-900">{proformaHistory.length}</span>
                          <span className="text-sm text-blue-700">invoices</span>
                        </div>
                        {proformaHistoryStatus !== 'all' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Status: {proformaHistoryStatus}
                          </span>
                        )}
                        {(proformaHistoryStartDate || proformaHistoryEndDate) && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Date Range: {proformaHistoryStartDate || 'Any'} to {proformaHistoryEndDate || 'Any'}
                          </span>
                        )}
                        {/* Show when no filters are applied */}
                        {proformaHistoryStatus === 'all' && !proformaHistoryStartDate && !proformaHistoryEndDate && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            No filters applied
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-blue-600">
                        {proformaHistoryLoading ? 'Loading...' : 
                         proformaHistoryStatus === 'all' && !proformaHistoryStartDate && !proformaHistoryEndDate ? 
                         'Showing all invoices' : 'Filters applied'}
                      </div>
                    </div>
                  </div>

                  {/* Invoice Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Invoice to Manage</label>
                    {proformaHistoryLoading ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span className="text-gray-600">Loading invoices...</span>
                      </div>
                    ) : proformaHistory.length === 0 ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-center text-gray-600">
                        <div className="mb-2">No invoices found.</div>
                        <div className="text-xs text-gray-500">
                          {proformaHistoryLoading ? 'Loading...' : 'Please create some proforma invoices first or check your connection.'}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          value={selectedInvoiceForManagement?.id || ''}
                          onChange={(e) => {
                            const invoice = proformaHistory.find(inv => inv.id === parseInt(e.target.value));
                            setSelectedInvoiceForManagement(invoice);
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                            selectedInvoiceForManagement ? 'border-green-300 bg-green-50' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Choose an invoice...</option>
                          {proformaHistory.map(invoice => (
                            <option key={invoice.id} value={invoice.id}>
                              {invoice.proforma_id} - {invoice.customer_name || invoice.customer?.name || 'N/A'} - ₵{parseFloat(invoice.grand_total).toFixed(2)} - {invoice.status || 'active'}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            Showing {proformaHistory.length} invoices. Use filters above to narrow down results.
                          </div>
                          {selectedInvoiceForManagement && (
                            <div className="text-xs text-green-600 font-medium">
                              ✓ Invoice selected: {selectedInvoiceForManagement.proforma_id}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Invoice Details */}
                  {selectedInvoiceForManagement && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-800 mb-3">Invoice Details</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">ID:</span>
                          <span className="ml-2 text-gray-800">{selectedInvoiceForManagement.proforma_id}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Status:</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs border ${getStatusBadgeColor(selectedInvoiceForManagement.status)}`}>
                            {getStatusBadgeText(selectedInvoiceForManagement.status)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Customer:</span>
                          <span className="ml-2 text-gray-800">{selectedInvoiceForManagement.customer_name || selectedInvoiceForManagement.customer?.name || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Amount:</span>
                          <span className="ml-2 text-gray-800">₵{parseFloat(selectedInvoiceForManagement.grand_total).toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Valid Until:</span>
                          <span className="ml-2 text-gray-800">{new Date(selectedInvoiceForManagement.valid_until).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Created:</span>
                          <span className="ml-2 text-gray-800">{new Date(selectedInvoiceForManagement.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Selection */}
                  {selectedInvoiceForManagement && (
                    <div>
                      
                      
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Action</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setInvoiceManagementAction('cancel')}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            invoiceManagementAction === 'cancel'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-300 hover:border-red-300 hover:bg-red-50'
                          }`}
                        >
                          <div className="text-center">
                            <div className="text-lg font-semibold">Cancel</div>
                            <div className="text-xs text-gray-600">Cancel the invoice</div>
                          </div>
                        </button>
                        <button
                          onClick={() => setInvoiceManagementAction('reactivate')}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            invoiceManagementAction === 'reactivate'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 hover:border-green-300 hover:bg-green-50'
                          }`}
                        >
                          <div className="text-center">
                            <div className="text-lg font-semibold">Reactivate</div>
                            <div className="text-xs text-gray-600">Reactivate cancelled invoice</div>
                          </div>
                        </button>
                        <button
                          onClick={() => setInvoiceManagementAction('extend')}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            invoiceManagementAction === 'extend'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-blue-300 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <div className="text-center">
                            <div className="text-lg font-semibold">Extend</div>
                            <div className="text-xs text-gray-600">Extend validity by 30 days</div>
                          </div>
                        </button>
                        <button
                          onClick={() => handleConvertToSale(selectedInvoiceForManagement)}
                          className="p-3 rounded-lg border-2 border-gray-300 hover:border-purple-300 hover:bg-purple-50 transition-all"
                        >
                          <div className="text-center">
                            <div className="text-lg font-semibold">Convert</div>
                            <div className="text-xs text-gray-600">Convert to sale</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Note */}
                  {selectedInvoiceForManagement && invoiceManagementAction && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note for {invoiceManagementAction} action
                      </label>
                      <textarea
                        value={invoiceManagementNote}
                        onChange={(e) => setInvoiceManagementNote(e.target.value)}
                        placeholder={`Enter note for ${invoiceManagementAction} action...`}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  )}

                  {/* Action Button */}
                  {selectedInvoiceForManagement && invoiceManagementAction && (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleInvoiceAction}
                        disabled={invoiceManagementLoading}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        {invoiceManagementLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Settings size={16} />
                        )}
                        <span>{invoiceManagementLoading ? 'Processing...' : `${invoiceManagementAction.charAt(0).toUpperCase() + invoiceManagementAction.slice(1)} Invoice`}</span>
                      </button>
                    </div>
                  )}
                  

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Sale Modal */}
      {showConvertToSaleModal && selectedInvoiceForManagement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Convert to Sale</h2>
                      <p className="text-white/80 text-sm">Convert proforma invoice to actual sale</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowConvertToSaleModal(false)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Invoice Summary */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Converting Invoice: {selectedInvoiceForManagement.proforma_id}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-blue-700">Customer:</span>
                        <span className="ml-2 text-blue-900">{selectedInvoiceForManagement.customer_name || selectedInvoiceForManagement.customer?.name || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-blue-700">Total Amount:</span>
                        <span className="ml-2 text-blue-900">₵{parseFloat(selectedInvoiceForManagement.grand_total).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Conversion Form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Customer Information */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-800">Customer Information</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                        <input
                          type="text"
                          value={conversionData.customer_name}
                          onChange={(e) => setConversionData(prev => ({ ...prev, customer_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          value={conversionData.customer_phone}
                          onChange={(e) => setConversionData(prev => ({ ...prev, customer_phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={conversionData.customer_email}
                          onChange={(e) => setConversionData(prev => ({ ...prev, customer_email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <textarea
                          value={conversionData.customer_address}
                          onChange={(e) => setConversionData(prev => ({ ...prev, customer_address: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>

                    {/* Sale Details */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-800">Sale Details</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                        <select
                          value={conversionData.payment_method}
                          onChange={(e) => setConversionData(prev => ({ ...prev, payment_method: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="Cash">Cash</option>
                          <option value="Credit">Credit</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Mobile Money">Mobile Money</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                        <select
                          value={conversionData.payment_status}
                          onChange={(e) => setConversionData(prev => ({ ...prev, payment_status: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="pending">Pending</option>
                          <option value="partial">Partial</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label>
                        <input
                          type="date"
                          value={conversionData.delivery_date}
                          onChange={(e) => setConversionData(prev => ({ ...prev, delivery_date: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
                        <textarea
                          value={conversionData.delivery_address}
                          onChange={(e) => setConversionData(prev => ({ ...prev, delivery_address: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={conversionData.notes}
                          onChange={(e) => setConversionData(prev => ({ ...prev, notes: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Items Summary */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Items to Convert</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium text-gray-700">Product</th>
                            <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-700">Qty</th>
                            <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-700">UoM</th>
                            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-medium text-gray-700">Unit Price</th>
                            <th className="border border-gray-300 px-3 py-2 text-right text-sm font-medium text-gray-700">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInvoiceForManagement.items?.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-3 py-2 text-sm text-gray-800">{item.product?.name || 'Unknown Product'}</td>
                              <td className="border border-gray-300 px-3 py-2 text-center text-sm text-gray-800">{formatQuantity(item.quantity)}</td>
                              <td className="border border-gray-300 px-3 py-2 text-center text-sm text-gray-800">{item.unit_of_measure}</td>
                              <td className="border border-gray-300 px-3 py-2 text-right text-sm text-gray-800">₵{parseFloat(item.price).toFixed(2)}</td>
                              <td className="border border-gray-300 px-3 py-2 text-right text-sm text-gray-800">₵{parseFloat(item.amount).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex space-x-3">
                <button
                  onClick={handleConvertInvoiceToSale}
                  disabled={invoiceManagementLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {invoiceManagementLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ShoppingCart size={16} />
                  )}
                  <span>{invoiceManagementLoading ? 'Converting...' : 'Convert to Sale'}</span>
                </button>
                <button
                  onClick={() => setShowConvertToSaleModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProformaInvoice;